import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { usePlatform } from '../contexts/PlatformContext';
import {
  ROLE_OPTIONS as BUILT_IN_ROLE_OPTIONS,
  roleLevel as levelForRole,
} from '../data/roleLevels';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { Badge } from '../components/common/Badge';
import { Modal } from '../components/common/Modal';
import { Pagination, usePaginationState } from '../components/common/Pagination';
import {
  Plus, Trash2, Edit2, Users, Shield, UserPlus, UserMinus,
  ChevronRight, Search, X, Check, AlertTriangle, Eye, Lock,
  Link2, Unlink, Layers, Building2, Star, Send, Mail, Clock, Copy, Activity,
  ClipboardCheck, FileText, Zap, Database, ChevronDown, ChevronUp,
} from 'lucide-react';
import * as api from '../services/api';
import { validatePassword, getPasswordRequirements, getPasswordStrength, PASSWORD_STRENGTH_LABELS, PASSWORD_STRENGTH_COLORS } from '../utils/passwordPolicy';
import PageHeader from '../components/common/PageHeader';
import PageContainer from '../components/common/PageContainer';
/* G95 — full v2 redesign of TeamPage. Module-level style tokens are
 * replaced below with v2-design-system aligned tokens so every internal
 * button / input / table cell / card inherits the v2 look without us
 * touching each call site. The chrome (page shell, header actions,
 * stat tiles, tab bar) uses real v2 components. Every hook, handler,
 * and api.* call from v1 is preserved verbatim. */
import {
  V2PageShell,
  TabNav as V2TabNav,
  Button as V2Button,
  Pill as V2Pill,
  MetricCard as V2MetricCard,
} from '../components/v2';

// ---------------------------------------------------------------------------
// G95 — v2-aligned design tokens (replaces v1 inline styles)
// All colors map to the v2 design system equivalents the rest of the
// platform uses: zinc/slate neutrals, action accent for primary,
// success/warning/danger for state, with the v2 6/8/12 spacing scale
// and 10px/11px/13px type scale.
// ---------------------------------------------------------------------------
const btnBase = {
  padding: '7px 12px',
  borderRadius: 6,
  border: '1px solid var(--v2-border, #e4e4e7)',
  background: 'var(--v2-surface, #ffffff)',
  color: 'var(--v2-ink, #18181b)',
  cursor: 'pointer',
  fontSize: 12,
  fontWeight: 500,
  lineHeight: 1.3,
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  transition: 'all 0.12s ease',
};
const btnPrimary = {
  ...btnBase,
  border: 'none',
  background: 'var(--v2-action, #18181b)',
  color: '#fff',
  fontWeight: 600,
};
const btnDanger = {
  ...btnBase,
  border: '1px solid var(--v2-danger-border, #fecaca)',
  background: 'var(--v2-danger-soft, #fef2f2)',
  color: 'var(--v2-danger, #b91c1c)',
};
const btnSuccess = {
  ...btnBase,
  border: '1px solid var(--v2-success-border, #a7f3d0)',
  background: 'var(--v2-success-soft, #ecfdf5)',
  color: 'var(--v2-success, #047857)',
};
const btnWarning = {
  ...btnBase,
  border: '1px solid var(--v2-warning-border, #fde68a)',
  background: 'var(--v2-warning-soft, #fffbeb)',
  color: 'var(--v2-warning, #92400e)',
};
const labelStyle = {
  display: 'block',
  fontSize: 11,
  fontWeight: 600,
  color: 'var(--v2-ink-muted, #71717a)',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  marginBottom: 6,
};
const inputStyle = {
  width: '100%',
  padding: '8px 12px',
  borderRadius: 6,
  border: '1px solid var(--v2-border, #e4e4e7)',
  fontSize: 13,
  background: 'var(--v2-surface, #ffffff)',
  color: 'var(--v2-ink, #18181b)',
  boxSizing: 'border-box',
};
const thStyle = {
  padding: '10px 14px',
  textAlign: 'left',
  fontSize: 10.5,
  fontWeight: 600,
  color: 'var(--v2-ink-muted, #71717a)',
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  background: 'var(--v2-muted, #fafafa)',
  borderBottom: '1px solid var(--v2-border-subtle, #f4f4f5)',
};
const tdStyle = {
  padding: '11px 14px',
  fontSize: 13,
  borderBottom: '1px solid var(--v2-border-subtle, #f4f4f5)',
};

// The built-in role taxonomy, ordered by tier, shared with TeamPageV2 and the
// member managers — src/data/roleLevels.js. See docs/RBAC_REDESIGN_V2.md.
const ROLE_OPTIONS = BUILT_IN_ROLE_OPTIONS;
/* G95 — re-tone role badges to v2 palette. Super Admin stays high-contrast
 * danger; Org / Tenant Admin map to action / trust; Developer to cyan;
 * Viewer to neutral muted. Same shape (bg/text/border) so existing call
 * sites work without changes.
 *
 * V208 / RBAC v2 — added tones for the 7 new roles (Platform Admin,
 * Instance Admin, Promoter, Approver, Compliance, Operator, Auditor).
 * Platform Admin shares Super Admin's danger tone (both are highest-
 * privilege wildcards); Instance Admin echoes Tenant Admin's trust tone;
 * the four new functional roles each get a distinct hue so they scan
 * cleanly in dense user lists. */
const ROLE_COLORS = {
  'Platform Admin': { bg: '#fef2f2', text: '#991b1b', border: '#fecaca' },
  'Super Admin':    { bg: '#fef2f2', text: '#991b1b', border: '#fecaca' },
  'Org Admin':      { bg: '#eff6ff', text: '#1d4ed8', border: '#bfdbfe' },
  'Tenant Admin':   { bg: '#f0f9ff', text: '#0369a1', border: '#bae6fd' },
  'Protect':        { bg: '#f0f9ff', text: '#0369a1', border: '#bae6fd' },
  'Instance Admin': { bg: '#f0f9ff', text: '#0369a1', border: '#bae6fd' },
  'Promoter':       { bg: '#ecfeff', text: '#0e7490', border: '#a5f3fc' },
  'Developer':      { bg: '#ecfeff', text: '#0e7490', border: '#a5f3fc' },
  // V208 functional roles
  'Approver':       { bg: '#fffbeb', text: '#92400e', border: '#fde68a' },
  'Compliance':     { bg: '#eef2ff', text: '#3730a3', border: '#c7d2fe' },
  'Operator':       { bg: '#f0fdfa', text: '#0f766e', border: '#99f6e4' },
  'Auditor':        { bg: '#f5f3ff', text: '#5b21b6', border: '#ddd6fe' },
  'Viewer':         { bg: '#f4f4f5', text: '#52525b', border: '#e4e4e7' },
};
const PHASE_MAP = { research: 'Build', build: 'Build', decide: 'Decide', act: 'Act' };
const ACCESS_LEVELS = ['execute', 'manage', 'admin'];

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------
export default function TeamPage() {
  const {
    users, groups, agents, assignments, connectors,
    connectorGroups, connectorAssignments,
    loadUsers, loadGroups, loadAgents, loadConnectors,
    createUser, updateUser, deleteUser: deleteUserApi,
    createGroup, updateGroup, deleteGroup: deleteGroupApi,
    addGroupMember, removeGroupMember,
    loadAssignments, createAssignment, removeAssignment,
    loadConnectorGroups, createConnectorGroup, updateConnectorGroup,
    deleteConnectorGroup: deleteConnectorGroupApi,
    addConnectorGroupMember, removeConnectorGroupMember,
    loadConnectorAssignments, createConnectorAssignment, removeConnectorAssignment,
    orgs, tenants, loadOrganizations, loadTenants,
    invitations, sendInvitation, loadInvitations, revokeInvitation,
    workflows, loadWorkflows,
    deployments, loadDeployments,
  } = usePlatform();
  const { user: authUser, session, hasPermission, canManageRole, currentRole, isSuperAdmin } = useAuth();
  const { addToast } = useToast();

  const [tab, setTab] = useState('users');
  const [searchTerm, setSearchTerm] = useState('');

  // Modal states
  const [showUserModal, setShowUserModal] = useState(false);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [showMemberSearch, setShowMemberSearch] = useState(null);  // groupId
  const [showAssignModal, setShowAssignModal] = useState(null);    // agentId
  const [editingUser, setEditingUser] = useState(null);
  const [editingGroup, setEditingGroup] = useState(null);
  const [expandedGroup, setExpandedGroup] = useState(null);
  const [expandedAgent, setExpandedAgent] = useState(null);
  const [memberSearch, setMemberSearch] = useState('');

  // Assignment sub-tab & workflow assignments state
  const [assignSubTab, setAssignSubTab] = useState('agents'); // agents | workflows | infrastructure
  const [workflowAssignments, setWorkflowAssignments] = useState({}); // { workflowId: [...] }
  const [expandedWorkflow, setExpandedWorkflow] = useState(null);
  const [showWfAssignModal, setShowWfAssignModal] = useState(null); // { workflowId, mode: 'userGroup' | 'agent' }
  const [wfAssignForm, setWfAssignForm] = useState({ userId: '', groupId: '', agentId: '', accessLevel: 'execute' });
  const [infraDeployments, setInfraDeployments] = useState([]);
  const [showDeployModal, setShowDeployModal] = useState(false);
  const [deployForm, setDeployForm] = useState({ agentId: '', orgId: '', tenantId: '', environment: 'production', gatewayUrl: '' });
  const [expandedOrg, setExpandedOrg] = useState(null);
  const [expandedTenant, setExpandedTenant] = useState(null);
  const pgWorkflowAssign = usePaginationState(10);
  const pgInfra = usePaginationState(10);

  // Connector Groups state
  const [showConnGroupModal, setShowConnGroupModal] = useState(false);
  const [editingConnGroup, setEditingConnGroup] = useState(null);
  const [expandedConnGroup, setExpandedConnGroup] = useState(null);
  const [connGroupForm, setConnGroupForm] = useState({ name: '', description: '' });
  const [showConnAssignModal, setShowConnAssignModal] = useState(null); // agentId
  const [connAssignForm, setConnAssignForm] = useState({ connectorId: '', connectorGroupId: '' });

  // Membership state for edit modal
  const [userMemberships, setUserMemberships] = useState([]);
  const [loadingMemberships, setLoadingMemberships] = useState(false);
  const [newMembership, setNewMembership] = useState({ orgId: '', tenantId: '', role: 'Viewer' });

  // Invite modal state
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteForm, setInviteForm] = useState({ firstName: '', lastName: '', email: '', orgId: '', tenantId: '', role: 'Developer' });
  const [inviteResult, setInviteResult] = useState(null);
  const [inviteError, setInviteError] = useState(null);
  const [inviteLoading, setInviteLoading] = useState(false);

  // Form states
  const [userForm, setUserForm] = useState({ firstName: '', lastName: '', email: '', password: '', confirmPassword: '', role: 'Developer' });
  const [groupForm, setGroupForm] = useState({ name: '', description: '' });
  const [assignForm, setAssignForm] = useState({ userId: '', groupId: '', accessLevel: 'execute' });

  // Pagination state for each tab / section
  const pgUsers = usePaginationState(10);
  const pgGroups = usePaginationState(10);
  const pgConnGroups = usePaginationState(10);
  const pgAssignments = usePaginationState(10);
  const pgReviewAgents = usePaginationState(10);
  const pgReviewWorkflows = usePaginationState(10);

  // Escalation Policies state
  const [escPolicies, setEscPolicies] = useState([]);
  const [escLoading, setEscLoading] = useState(false);
  const [showEscModal, setShowEscModal] = useState(false);
  const [editingEscPolicy, setEditingEscPolicy] = useState(null);
  const [escForm, setEscForm] = useState({ name: '', description: '', scope: 'DEFAULT', scopeFilter: '', tiers: [{ tier: 1, name: '', targetType: 'ROLE', targetValue: 'Org Admin', slaHours: 24 }] });
  const pgEscPolicies = usePaginationState(10);

  // IGA Sync state
  const [showIgaModal, setShowIgaModal] = useState(false);
  const [igaSyncing, setIgaSyncing] = useState(false);
  const [igaSource, setIgaSource] = useState('');
  const [igaConnectorId, setIgaConnectorId] = useState('');
  const [igaSyncDirection, setIgaSyncDirection] = useState('INBOUND'); // INBOUND | OUTBOUND | BIDIRECTIONAL
  const [scimEndpointCopied, setScimEndpointCopied] = useState(false);

  // IGA-compatible connectors (filter from the connectors list)
  const igaConnectors = useMemo(() => {
    if (!connectors) return [];
    return connectors.filter(c => {
      const type = (c.connectorType || c.type || '').toLowerCase();
      const name = (c.name || '').toLowerCase();
      return type.includes('iga') || type.includes('sailpoint') || type.includes('saviynt')
        || type.includes('cyberark') || type.includes('identity')
        || name.includes('sailpoint') || name.includes('saviynt') || name.includes('cyberark')
        || name.includes('okta') || name.includes('iga');
    });
  }, [connectors]);

  // Access Review state
  const [reviewUserId, setReviewUserId] = useState(null);
  const [reviewMemberships, setReviewMemberships] = useState([]);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewWorkflowAssignments, setReviewWorkflowAssignments] = useState([]);
  const [reviewSections, setReviewSections] = useState({ memberships: true, agents: true, workflows: true, connectors: true, permissions: false });

  const tenantId = session?.tenantId;
  const orgId = session?.orgId;
  const userId = session?.userId;

  // Permissions
  const canManageUsers = hasPermission('user.manage');
  const canCreateUsers = hasPermission('user.create');
  const canEditUsers = hasPermission('user.edit');
  const canDeleteUsers = hasPermission('user.delete');
  const canToggleUsers = hasPermission('user.toggle');
  const canManageGroups = hasPermission('group.manage');
  const canCreateGroups = hasPermission('group.create');
  const canEditGroups = hasPermission('group.edit');
  const canDeleteGroups = hasPermission('group.delete');
  const canManageMembers = hasPermission('group.members');
  const canManageAssignments = hasPermission('assignment.manage');

  useEffect(() => {
    // Super Admin should see ALL users across all tenants/orgs
    loadUsers(isSuperAdmin() ? {} : { tenantId });
    loadGroups({ tenantId });
    loadAgents({ orgId, tenantId });
    loadConnectors({ tenantId, orgId });
    loadConnectorGroups(tenantId);
    loadOrganizations();
    loadTenants();
    loadInvitations();
  }, [loadUsers, loadGroups, loadAgents, loadConnectors, loadConnectorGroups, loadOrganizations, loadTenants, loadInvitations, orgId, tenantId, isSuperAdmin]);

  useEffect(() => {
    if (tab === 'assignments') {
      if (agents.length > 0) {
        agents.forEach((a) => {
          loadAssignments(a.id);
          loadConnectorAssignments(a.id);
        });
      }
      // Load workflows + their assignments for the Workflows sub-tab
      loadWorkflows({ orgId, tenantId });
      // Load deployments for the Infrastructure sub-tab
      loadDeployments({});
    }
  }, [tab, agents, loadAssignments, loadConnectorAssignments, loadWorkflows, loadDeployments, orgId, tenantId]);

  // Load escalation policies when tab is opened
  const fetchEscPolicies = useCallback(async () => {
    setEscLoading(true);
    try {
      const res = await api.ccGetEscalationPolicies();
      if (res.ok) setEscPolicies(await res.json());
    } catch (_) { /* non-critical */ }
    setEscLoading(false);
  }, []);

  useEffect(() => {
    if (tab === 'escalation') fetchEscPolicies();
  }, [tab, fetchEscPolicies]);

  // Load workflows + assignments when Access Review tab is opened
  useEffect(() => {
    if (tab === 'accessReview') {
      loadWorkflows({ orgId, tenantId });
      // Load assignments for all agents so we can cross-reference
      agents.forEach((a) => {
        loadAssignments(a.id);
        loadConnectorAssignments(a.id);
      });
    }
  }, [tab, orgId, tenantId, agents, loadWorkflows, loadAssignments, loadConnectorAssignments]);

  // Load data when a user is selected for review
  const handleSelectReviewUser = useCallback(async (uid) => {
    setReviewUserId(uid);
    if (!uid) { setReviewMemberships([]); setReviewWorkflowAssignments([]); return; }
    setReviewLoading(true);
    try {
      const mResp = await api.getUserMemberships(uid);
      const memberships = mResp?.data || mResp;
      setReviewMemberships(Array.isArray(memberships) ? memberships : []);
      // Load workflow assignments
      const wfAssignments = [];
      for (const wf of workflows) {
        try {
          const waResp = await api.getWorkflowAssignments(wf.id);
          const wa = waResp?.data || waResp;
          if (Array.isArray(wa)) wfAssignments.push(...wa.map((a) => ({ ...a, workflowName: wf.name, workflowId: wf.id })));
        } catch (_) { /* skip */ }
      }
      setReviewWorkflowAssignments(wfAssignments);
    } catch (err) {
      console.error('Failed to load review data:', err);
    } finally {
      setReviewLoading(false);
    }
  }, [workflows]);

  // -----------------------------------------------------------------------
  // User Handlers
  // -----------------------------------------------------------------------
  const openAddUser = () => {
    setEditingUser(null);
    setUserForm({ firstName: '', lastName: '', email: '', password: '', confirmPassword: '', role: 'Developer' });
    setShowUserModal(true);
  };

  const openInviteUser = () => {
    const defaultOrg = orgs[0]?.id || '';
    const defaultTenant = tenants.filter((t) => t.orgId === defaultOrg)[0]?.id || '';
    setInviteForm({ firstName: '', lastName: '', email: '', orgId: defaultOrg, tenantId: defaultTenant, role: 'Developer' });
    setInviteResult(null);
    setInviteError(null);
    setInviteLoading(false);
    setShowInviteModal(true);
  };

  const handleSendInvite = async () => {
    setInviteError(null);
    if (!inviteForm.firstName.trim() || !inviteForm.email.trim()) {
      setInviteError('First name and email are required.'); return;
    }
    if (!inviteForm.orgId) {
      setInviteError('Organization is required.'); return;
    }
    if (!inviteForm.tenantId) {
      setInviteError('Tenant is required.'); return;
    }
    setInviteLoading(true);
    const result = await sendInvitation({
      firstName: inviteForm.firstName,
      lastName: inviteForm.lastName,
      email: inviteForm.email,
      tenantId: inviteForm.tenantId,
      orgId: inviteForm.orgId,
      role: inviteForm.role,
    });
    setInviteLoading(false);
    if (result?.ok) {
      setInviteError(null);
      setInviteResult(result.data);
      addToast(result.reInvite
        ? `Re-invitation sent to ${inviteForm.email} (existing user)`
        : `Invitation sent to ${inviteForm.email}`, 'success');
    } else {
      // Show error inline in the modal — especially for active user blocks
      setInviteError(result?.error || 'Invitation failed. Please try again.');
    }
  };

  const openEditUser = async (user) => {
    setEditingUser(user);
    setUserForm({
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      email: user.email || '',
      role: user.role || 'Viewer',
    });
    setShowUserModal(true);
    // Load memberships
    setLoadingMemberships(true);
    setUserMemberships([]);
    try {
      const res = await api.getUserMemberships(user.id);
      if (res.ok) setUserMemberships(res.data || []);
    } catch (e) { console.error('Failed to load memberships', e); }
    setLoadingMemberships(false);
  };

  const handleSaveUser = async () => {
    if (!userForm.firstName.trim() || !userForm.email.trim()) {
      addToast('First name and email are required', 'warn'); return;
    }
    if (!editingUser) {
      const pwResult = validatePassword(userForm.password);
      if (!pwResult.valid) {
        addToast(`Password: ${pwResult.errors[0]}`, 'warn'); return;
      }
      if (userForm.password !== userForm.confirmPassword) {
        addToast('Passwords do not match', 'warn'); return;
      }
    }
    if (editingUser && !canEditUsers) { addToast('No permission to edit users', 'error'); return; }
    if (!editingUser && !canCreateUsers) { addToast('No permission to create users', 'error'); return; }

    if (editingUser) {
      const result = await updateUser(editingUser.id, {
        name: `${userForm.firstName} ${userForm.lastName}`.trim(),
        firstName: userForm.firstName,
        lastName: userForm.lastName,
        role: userForm.role,
      });
      if (result?.ok) {
        addToast(`User "${userForm.firstName}" updated`, 'success');
        loadUsers(isSuperAdmin() ? {} : { tenantId });
      } else {
        addToast(`Update failed: ${result?.error || 'Unknown'}`, 'error');
      }
    } else {
      const result = await createUser({
        firstName: userForm.firstName, lastName: userForm.lastName,
        name: `${userForm.firstName} ${userForm.lastName}`.trim(),
        email: userForm.email, password: userForm.password,
        role: userForm.role, status: 'active', tenantId,
      });
      if (result?.ok) {
        addToast(`User "${userForm.firstName}" created`, 'success');
      } else {
        addToast(`Create failed: ${result?.error || 'Unknown'}`, 'error');
      }
    }
    setShowUserModal(false);
  };

  const handleDeleteUser = async () => {
    if (!canDeleteUsers) { addToast('No permission to delete users', 'error'); return; }
    const item = showDeleteConfirm?.item;
    const result = await deleteUserApi(item.id);
    if (result?.ok) {
      addToast(`User "${item.name || item.email}" deleted`, 'success');
      loadUsers(isSuperAdmin() ? {} : { tenantId });
    } else {
      addToast(`Delete failed: ${result?.error || 'Unknown error'}`, 'error');
    }
    setShowDeleteConfirm(null);
  };

  const handleToggleStatus = async (user) => {
    if (!canToggleUsers) { addToast('No permission to toggle user status', 'error'); return; }
    // Prevent toggling invited users — they must register first
    if (user.status === 'invited') {
      addToast('Invited users must complete registration before being activated', 'warn');
      return;
    }
    const newStatus = user.status === 'active' ? 'inactive' : 'active';
    const result = await updateUser(user.id, { status: newStatus });
    if (result?.ok) {
      addToast(`${user.name || user.email} set to ${newStatus}`, 'success');
      // Note: updateUser already updates local state via setUsers — no need to reload
      // Reloading from backend would wipe client-side-created users
    } else {
      addToast(`Toggle failed: ${result?.error || 'Unknown'}`, 'error');
    }
  };

  // -----------------------------------------------------------------------
  // Membership Handlers
  // -----------------------------------------------------------------------
  const handleAddMembership = async () => {
    if (!editingUser || !newMembership.orgId) {
      addToast('Organization is required', 'warn'); return;
    }
    const res = await api.addUserMembership(editingUser.id, {
      orgId: newMembership.orgId,
      tenantId: newMembership.tenantId || null,
      role: newMembership.role || 'Viewer',
      isDefault: userMemberships.length === 0,
    });
    if (res?.ok) {
      addToast('Membership added', 'success');
      setUserMemberships((prev) => [...prev, res.data]);
      setNewMembership({ orgId: '', tenantId: '', role: 'Viewer' });
    } else {
      addToast(`Failed: ${res?.error || 'Unknown'}`, 'error');
    }
  };

  const handleRemoveMembership = async (membershipId) => {
    if (!editingUser) return;
    const res = await api.removeUserMembership(editingUser.id, membershipId);
    if (res?.ok) {
      addToast('Membership removed', 'success');
      setUserMemberships((prev) => prev.filter((m) => m.id !== membershipId));
    } else {
      addToast(`Failed: ${res?.error || 'Unknown'}`, 'error');
    }
  };

  const handleSetDefaultMembership = async (membershipId) => {
    if (!editingUser) return;
    const res = await api.setDefaultMembership(editingUser.id, membershipId);
    if (res?.ok) {
      addToast('Default membership updated', 'success');
      setUserMemberships((prev) => prev.map((m) => ({ ...m, isDefault: m.id === membershipId })));
      loadUsers({ tenantId });
    } else {
      addToast(`Failed: ${res?.error || 'Unknown'}`, 'error');
    }
  };

  // Filtered tenants by selected org for new membership form
  const filteredTenantsForOrg = useMemo(() => {
    if (!newMembership.orgId) return tenants || [];
    return (tenants || []).filter((t) => t.orgId === newMembership.orgId || !t.orgId);
  }, [tenants, newMembership.orgId]);

  // -----------------------------------------------------------------------
  // Group Handlers
  // -----------------------------------------------------------------------
  const openAddGroup = () => {
    setEditingGroup(null);
    setGroupForm({ name: '', description: '' });
    setShowGroupModal(true);
  };

  const openEditGroup = (group) => {
    setEditingGroup(group);
    setGroupForm({ name: group.name || '', description: group.description || '' });
    setShowGroupModal(true);
  };

  const handleSaveGroup = async () => {
    if (!groupForm.name.trim()) { addToast('Group name required', 'warn'); return; }
    if (editingGroup && !canEditGroups) { addToast('No permission to edit groups', 'error'); return; }
    if (!editingGroup && !canCreateGroups) { addToast('No permission to create groups', 'error'); return; }

    if (editingGroup) {
      const result = await updateGroup(editingGroup.id, groupForm);
      if (result?.ok) { addToast(`Group "${groupForm.name}" updated`, 'success'); }
      else addToast(`Update failed: ${result?.error || 'Unknown'}`, 'error');
    } else {
      const result = await createGroup({ ...groupForm, tenantId });
      if (result?.ok) { addToast(`Group "${groupForm.name}" created`, 'success'); }
      else addToast(`Create failed: ${result?.error || 'Unknown'}`, 'error');
    }
    setShowGroupModal(false);
  };

  const handleDeleteGroup = async () => {
    if (!canDeleteGroups) { addToast('No permission to delete groups', 'error'); return; }
    const item = showDeleteConfirm?.item;
    const result = await deleteGroupApi(item.id);
    if (result?.ok) addToast(`Group "${item.name}" deleted`, 'success');
    else addToast(`Delete failed: ${result?.error || 'Unknown'}`, 'error');
    setShowDeleteConfirm(null);
  };

  const handleAddMember = async (groupId, memberId) => {
    const result = await addGroupMember(groupId, memberId);
    if (result?.ok) { addToast('Member added', 'success'); setMemberSearch(''); }
    else addToast(`Failed: ${result?.error || 'Unknown'}`, 'error');
  };

  const handleRemoveMember = async (groupId, memberId) => {
    const result = await removeGroupMember(groupId, memberId);
    if (result?.ok) addToast('Member removed', 'success');
    else addToast(`Failed: ${result?.error || 'Unknown'}`, 'error');
  };

  // -----------------------------------------------------------------------
  // Assignment Handlers
  // -----------------------------------------------------------------------
  const handleCreateAssignment = async (overrideFlag = false) => {
    if (!canManageAssignments) { addToast('No permission to manage assignments', 'error'); return; }
    if (!assignForm.userId && !assignForm.groupId) {
      addToast('Select a user or group', 'warn'); return;
    }
    const agentId = showAssignModal;
    // Clean empty strings to avoid UUID parse errors on backend
    const payload = { accessLevel: assignForm.accessLevel || 'execute' };
    if (assignForm.userId) payload.userId = assignForm.userId;
    if (assignForm.groupId) payload.groupId = assignForm.groupId;
    // assignedBy: prefer session.userId, fall back to authUser.id
    const currentUserId = userId || authUser?.id;
    if (currentUserId) payload.assignedBy = currentUserId;
    if (overrideFlag) payload.override = 'true';

    const result = await createAssignment(agentId, payload);

    // ── Duplicate detection: 409 Conflict ────────────────────────────
    if (result?.duplicate) {
      const targetName = assignForm.userId
        ? (users.find(u => u.id === assignForm.userId)?.name || assignForm.userId)
        : (groups.find(g => g.id === assignForm.groupId)?.name || assignForm.groupId);
      const existingLevel = result.existingAccessLevel || 'unknown';
      const newLevel = assignForm.accessLevel || 'execute';
      const msg = existingLevel === newLevel
        ? `"${targetName}" is already assigned to this agent with access level "${existingLevel}". Duplicate assignment not allowed.`
        : `"${targetName}" is already assigned with access level "${existingLevel}". Do you want to override it with "${newLevel}"?`;

      if (existingLevel === newLevel) {
        // Exact duplicate — just warn, don't allow
        addToast(msg, 'warn');
        return;
      }
      // Different access level — offer to override
      const confirmed = window.confirm(msg);
      if (confirmed) {
        return handleCreateAssignment(true); // retry with override flag
      }
      return; // user cancelled
    }

    if (result?.ok) {
      addToast('Assignment created', 'success');
      loadAssignments(agentId);
    } else {
      addToast(`Failed: ${result?.error || 'Unknown'}`, 'error');
    }
    setShowAssignModal(null);
    setAssignForm({ userId: '', groupId: '', accessLevel: 'execute' });
  };

  const handleRemoveAssignment = async (agentId, assignmentId) => {
    const result = await removeAssignment(agentId, assignmentId);
    if (result?.ok) {
      addToast('Assignment removed', 'success');
      loadAssignments(agentId);
    } else {
      addToast(`Failed: ${result?.error || 'Unknown'}`, 'error');
    }
  };

  // -----------------------------------------------------------------------
  // Connector Group Handlers
  // -----------------------------------------------------------------------
  const openAddConnGroup = () => {
    setEditingConnGroup(null);
    setConnGroupForm({ name: '', description: '' });
    setShowConnGroupModal(true);
  };

  const openEditConnGroup = (group) => {
    setEditingConnGroup(group);
    setConnGroupForm({ name: group.name || '', description: group.description || '' });
    setShowConnGroupModal(true);
  };

  const handleSaveConnGroup = async () => {
    if (!connGroupForm.name.trim()) { addToast('Group name required', 'warn'); return; }
    if (editingConnGroup) {
      const result = await updateConnectorGroup(editingConnGroup.id, connGroupForm);
      if (result?.ok) { addToast(`Connector group "${connGroupForm.name}" updated`, 'success'); }
      else addToast(`Update failed: ${result?.error || 'Unknown'}`, 'error');
    } else {
      const result = await createConnectorGroup({ ...connGroupForm, tenantId, orgId });
      if (result?.ok) { addToast(`Connector group "${connGroupForm.name}" created`, 'success'); }
      else addToast(`Create failed: ${result?.error || 'Unknown'}`, 'error');
    }
    setShowConnGroupModal(false);
    loadConnectorGroups(tenantId);
  };

  const handleDeleteConnGroup = async (group) => {
    const result = await deleteConnectorGroupApi(group.id);
    if (result?.ok) addToast(`Connector group "${group.name}" deleted`, 'success');
    else addToast(`Delete failed: ${result?.error || 'Unknown'}`, 'error');
    loadConnectorGroups(tenantId);
  };

  const handleAddConnGroupMember = async (groupId, connectorId) => {
    const result = await addConnectorGroupMember(groupId, connectorId);
    if (result?.ok) { addToast('Connector added to group', 'success'); loadConnectorGroups(tenantId); }
    else addToast(`Failed: ${result?.error || 'Unknown'}`, 'error');
  };

  const handleRemoveConnGroupMember = async (groupId, connectorId) => {
    const result = await removeConnectorGroupMember(groupId, connectorId);
    if (result?.ok) { addToast('Connector removed from group', 'success'); loadConnectorGroups(tenantId); }
    else addToast(`Failed: ${result?.error || 'Unknown'}`, 'error');
  };

  // -----------------------------------------------------------------------
  // Connector Assignment Handlers
  // -----------------------------------------------------------------------
  const handleCreateConnectorAssignment = async (overrideFlag = false) => {
    if (!connAssignForm.connectorId && !connAssignForm.connectorGroupId) {
      addToast('Select a connector or connector group', 'warn'); return;
    }
    const agentId = showConnAssignModal;
    const payload = {};
    if (connAssignForm.connectorId) payload.connectorId = connAssignForm.connectorId;
    if (connAssignForm.connectorGroupId) payload.connectorGroupId = connAssignForm.connectorGroupId;
    const currentUserId = userId || authUser?.id;
    if (currentUserId) payload.assignedBy = currentUserId;
    if (overrideFlag) payload.override = 'true';

    const result = await createConnectorAssignment(agentId, payload);

    // ── Duplicate detection: 409 Conflict ────────────────────────────
    if (result?.duplicate) {
      const targetName = connAssignForm.connectorId
        ? (connectors.find(c => c.id === connAssignForm.connectorId)?.name || connAssignForm.connectorId)
        : (connectorGroups.find(g => g.id === connAssignForm.connectorGroupId)?.name || connAssignForm.connectorGroupId);
      const msg = `"${targetName}" is already assigned to this agent. This is a duplicate assignment and is not allowed.`;
      addToast(msg, 'warn');
      return;
    }

    if (result?.ok) {
      addToast('Connector assignment created', 'success');
      loadConnectorAssignments(agentId);
    } else {
      addToast(`Failed: ${result?.error || 'Unknown'}`, 'error');
    }
    setShowConnAssignModal(null);
    setConnAssignForm({ connectorId: '', connectorGroupId: '' });
  };

  const handleRemoveConnectorAssignment = async (agentId, assignmentId) => {
    const result = await removeConnectorAssignment(agentId, assignmentId);
    if (result?.ok) {
      addToast('Connector assignment removed', 'success');
      loadConnectorAssignments(agentId);
    } else {
      addToast(`Failed: ${result?.error || 'Unknown'}`, 'error');
    }
  };

  // -----------------------------------------------------------------------
  // Workflow Assignment Handlers
  // -----------------------------------------------------------------------
  const loadWfAssignments = useCallback(async (workflowId) => {
    const res = await api.getWorkflowAssignments(workflowId);
    if (res?.ok) setWorkflowAssignments(prev => ({ ...prev, [workflowId]: res.data || [] }));
  }, []);

  const handleCreateWfAssignment = async () => {
    if (!canManageAssignments) { addToast('No permission', 'error'); return; }
    const wfId = showWfAssignModal?.workflowId;
    if (!wfAssignForm.userId && !wfAssignForm.groupId && !wfAssignForm.agentId) {
      addToast('Select a user, group, or agent', 'warn'); return;
    }
    const payload = { accessLevel: wfAssignForm.accessLevel || 'execute' };
    if (wfAssignForm.userId) payload.userId = wfAssignForm.userId;
    if (wfAssignForm.groupId) payload.groupId = wfAssignForm.groupId;
    if (wfAssignForm.agentId) payload.agentId = wfAssignForm.agentId;
    const currentUserId = userId || authUser?.id;
    if (currentUserId) payload.assignedBy = currentUserId;

    const result = await api.createWorkflowAssignment(wfId, payload);
    if (result?.ok) {
      addToast('Workflow assignment created', 'success');
      loadWfAssignments(wfId);
    } else {
      addToast(`Failed: ${result?.error || 'Unknown'}`, 'error');
    }
    setShowWfAssignModal(null);
    setWfAssignForm({ userId: '', groupId: '', agentId: '', accessLevel: 'execute' });
  };

  const handleRemoveWfAssignment = async (workflowId, assignmentId) => {
    const result = await api.removeWorkflowAssignment(assignmentId);
    if (result?.ok) {
      addToast('Workflow assignment removed', 'success');
      loadWfAssignments(workflowId);
    } else {
      addToast(`Failed: ${result?.error || 'Unknown'}`, 'error');
    }
  };

  // Load workflow assignments when expanding a workflow
  useEffect(() => {
    if (expandedWorkflow) loadWfAssignments(expandedWorkflow);
  }, [expandedWorkflow, loadWfAssignments]);

  // -----------------------------------------------------------------------
  // Infrastructure / Deployment Handlers
  // -----------------------------------------------------------------------
  const handleCreateDeployment = async () => {
    if (!deployForm.agentId || !deployForm.tenantId) {
      addToast('Agent and tenant are required', 'warn'); return;
    }
    const result = await api.deployFromAgent(deployForm.agentId, {
      tenantId: deployForm.tenantId,
      environment: deployForm.environment || 'production',
      gatewayUrl: deployForm.gatewayUrl || null,
      version: '1.0.0',
    });
    if (result?.ok) {
      addToast('Deployment created', 'success');
      loadDeployments({});
    } else {
      addToast(`Deploy failed: ${result?.error || 'Unknown'}`, 'error');
    }
    setShowDeployModal(false);
    setDeployForm({ agentId: '', orgId: '', tenantId: '', environment: 'production', gatewayUrl: '' });
  };

  const handleDeleteDeployment = async (deploymentId) => {
    const result = await api.deleteDeployment(deploymentId);
    if (result?.ok) {
      addToast('Deployment deleted', 'success');
      loadDeployments({});
    } else {
      addToast(`Delete failed: ${result?.error || 'Unknown'}`, 'error');
    }
  };

  const handleUpdateDeploymentStatus = async (deploymentId, status) => {
    const result = await api.updateDeploymentStatus(deploymentId, status);
    if (result?.ok) {
      addToast(`Status updated to ${status}`, 'success');
      loadDeployments({});
    } else {
      addToast(`Update failed: ${result?.error || 'Unknown'}`, 'error');
    }
  };

  // Build org → tenant → deployment hierarchy for infrastructure tab
  const infraHierarchy = useMemo(() => {
    const deploys = deployments || [];
    const hierarchy = (orgs || []).map(org => {
      const orgTenants = (tenants || []).filter(t => t.orgId === org.id).map(t => ({
        ...t,
        deployments: deploys.filter(d => d.tenantId === t.id),
      }));
      return { ...org, tenants: orgTenants, totalDeployments: orgTenants.reduce((s, t) => s + t.deployments.length, 0) };
    });
    return hierarchy;
  }, [orgs, tenants, deployments]);

  // Filtered tenants by selected org for deploy modal
  const deployFilteredTenants = useMemo(() => {
    if (!deployForm.orgId) return tenants || [];
    return (tenants || []).filter(t => t.orgId === deployForm.orgId);
  }, [tenants, deployForm.orgId]);

  // Publishable agents for deploy modal (Act phase + published)
  const deployableAgents = useMemo(() =>
    (agents || []).filter(a => (a.phase || '').toLowerCase() === 'act' && a.published),
  [agents]);

  // -----------------------------------------------------------------------
  // Org / Tenant lookup maps for user table
  // -----------------------------------------------------------------------
  const tenantMap = useMemo(() => {
    const m = {};
    (tenants || []).forEach((t) => { m[t.id] = t; });
    return m;
  }, [tenants]);
  const orgMap = useMemo(() => {
    const m = {};
    (orgs || []).forEach((o) => { m[o.id] = o; });
    return m;
  }, [orgs]);
  const getUserOrg = (user) => {
    const t = tenantMap[user.tenantId];
    if (t && t.orgId) return orgMap[t.orgId]?.name || '—';
    return '—';
  };
  const getUserTenant = (user) => {
    const t = tenantMap[user.tenantId];
    return t?.name || '—';
  };

  // -----------------------------------------------------------------------
  // Filters
  // -----------------------------------------------------------------------
  const filteredUsers = useMemo(() => {
    if (!searchTerm) return users;
    const t = searchTerm.toLowerCase();
    return users.filter((u) =>
      (u.name || '').toLowerCase().includes(t) ||
      (u.email || '').toLowerCase().includes(t) ||
      (u.role || '').toLowerCase().includes(t));
  }, [users, searchTerm]);

  const filteredGroups = useMemo(() => {
    if (!searchTerm) return groups;
    const t = searchTerm.toLowerCase();
    return groups.filter((g) =>
      (g.name || '').toLowerCase().includes(t) ||
      (g.description || '').toLowerCase().includes(t));
  }, [groups, searchTerm]);

  // Reset pagination when search/filter changes
  useEffect(() => { pgUsers.setPage(0); pgGroups.setPage(0); }, [searchTerm]); // eslint-disable-line

  // Member search: users NOT already in the group
  const memberSearchResults = useMemo(() => {
    if (!showMemberSearch || !memberSearch.trim()) return [];
    const group = groups.find((g) => g.id === showMemberSearch);
    const existingIds = (group?.members || []).map((m) => m.id || m);
    const t = memberSearch.toLowerCase();
    return users
      .filter((u) => !existingIds.includes(u.id))
      .filter((u) =>
        (u.name || '').toLowerCase().includes(t) ||
        (u.email || '').toLowerCase().includes(t))
      .slice(0, 8);
  }, [showMemberSearch, memberSearch, groups, users]);

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------
  /* G95 — v2 chrome. Tabs are computed once to keep badge counts live;
   * action buttons in the header are tab-aware (matching v1 semantics). */
  const v2Tabs = [
    { id: 'users',           label: 'Users',             badge: users.length || undefined },
    { id: 'groups',          label: 'Groups',            badge: groups.length || undefined },
    { id: 'connectorGroups', label: 'Connector Groups',  badge: connectorGroups.length || undefined },
    { id: 'assignments',     label: 'Assignments',       badge: agents.length || undefined },
    { id: 'accessReview',    label: 'Access Review' },
    { id: 'escalation',      label: 'Escalation Chains', badge: escPolicies.length || undefined },
  ];

  const headerActions = (() => {
    if (tab === 'users' && canCreateUsers) {
      return (
        <>
          <V2Button variant="primary" size="sm" iconLeft={<Send size={14} />} onClick={openInviteUser}>
            Invite User
          </V2Button>
          <V2Button variant="secondary" size="sm" iconLeft={<Plus size={14} />} onClick={openAddUser}>
            Add User
          </V2Button>
        </>
      );
    }
    if (tab === 'groups' && canCreateGroups) {
      return (
        <V2Button variant="primary" size="sm" iconLeft={<Plus size={14} />} onClick={openAddGroup}>
          Add Group
        </V2Button>
      );
    }
    if (tab === 'connectorGroups') {
      return (
        <V2Button variant="primary" size="sm" iconLeft={<Plus size={14} />} onClick={openAddConnGroup}>
          Add Connector Group
        </V2Button>
      );
    }
    return null;
  })();

  return (
    <V2PageShell
      title="Team Management"
      subtitle="Manage users, groups, RBAC assignments, escalation chains, and infrastructure deployments."
      tags={<V2Pill tone="cyan" size="sm">Administer</V2Pill>}
      actions={headerActions}
    >
      {/* Stats Row — v2 MetricCards. Tones map to platform semantics:
        * cyan for users, action for groups, trust for connector groups,
        * neutral for agents, warning for current role. */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
        <V2MetricCard
          label="Users"
          value={users.length}
          tone="cyan"
          delta={{ value: `${users.filter((u) => u.status === 'active').length} active`, direction: 'flat', intent: 'neutral' }}
        />
        <V2MetricCard
          label="Groups"
          value={groups.length}
          tone="action"
          delta={{ value: `${groups.reduce((s, g) => s + (g.members?.length || 0), 0)} memberships`, direction: 'flat', intent: 'neutral' }}
        />
        <V2MetricCard
          label="Conn. Groups"
          value={connectorGroups.length}
          tone="trust"
          delta={{ value: `${connectors.length} connectors`, direction: 'flat', intent: 'neutral' }}
        />
        <V2MetricCard
          label="Agents"
          value={agents.length}
          tone="muted"
        />
        <V2MetricCard
          label="Your Role"
          value={currentRole}
          tone="warning"
        />
      </div>

      {/* v2 TabNav — accessible tablist with arrow-key navigation */}
      <V2TabNav
        tabs={v2Tabs}
        activeId={tab}
        onChange={(id) => { setTab(id); setSearchTerm(''); }}
        ariaLabel="Team Management sections"
      />

      <div className="my-4" />

      {/* Search */}
      {(tab === 'users' || tab === 'groups' || tab === 'connectorGroups') && (
        <div style={{ marginBottom: 20, position: 'relative', maxWidth: 400 }}>
          <Search size={14} style={{ position: 'absolute', left: 12, top: 10, color: '#a1a1aa' }} />
          <input placeholder={`Search ${tab}...`} value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ ...inputStyle, paddingLeft: 34 }} />
          {searchTerm && (
            <X size={14} style={{ position: 'absolute', right: 12, top: 10, color: '#a1a1aa', cursor: 'pointer' }}
              onClick={() => setSearchTerm('')} />
          )}
        </div>
      )}

      {/* ============ USERS TAB ============ */}
      {tab === 'users' && (
        <div style={{ background: '#fff', border: '1px solid #e4e4e7', borderRadius: 8, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#fafaf9', borderBottom: '1px solid #e4e4e7' }}>
                {['Name', 'Email', 'Org / Tenant', 'Role', 'Status', 'Actions'].map((h) => (
                  <th key={h} style={thStyle}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredUsers.length === 0 ? (
                <tr><td colSpan={6} style={{ ...tdStyle, textAlign: 'center', color: '#a1a1aa', padding: 40 }}>
                  {searchTerm ? 'No users match your search' : 'No users found'}
                </td></tr>
              ) : pgUsers.paginate(filteredUsers).map((user) => {
                const canEdit = canEditUsers && canManageRole(user.role);
                const canDel = canDeleteUsers && canManageRole(user.role);
                const canToggle = canToggleUsers && canManageRole(user.role);
                return (
                  <tr key={user.id} style={{ borderBottom: '1px solid #f4f4f5' }}>
                    <td style={tdStyle}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <Avatar name={user.firstName || user.name} />
                        <div>
                          <div style={{ fontWeight: 600, color: '#18181b', fontSize: 13 }}>
                            {user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'Unnamed'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td style={{ ...tdStyle, fontSize: 12, color: '#52525b' }}>{user.email}</td>
                    <td style={tdStyle}>
                      <div style={{ fontSize: 12, lineHeight: 1.5 }}>
                        <div style={{ fontWeight: 600, color: '#18181b' }}>{getUserOrg(user)}</div>
                        <div style={{ color: '#71717a', fontSize: 11 }}>{getUserTenant(user)}</div>
                      </div>
                    </td>
                    <td style={tdStyle}><RoleBadge role={user.role} /></td>
                    <td style={tdStyle}>
                      {canToggle ? (
                        <button onClick={() => handleToggleStatus(user)}
                          style={{ ...btnBase, border: 'none', background: 'none', padding: '2px 0' }}
                          title={user.status === 'invited' ? 'User must complete registration first' : `Toggle to ${user.status === 'active' ? 'inactive' : 'active'}`}>
                          <Badge status={user.status === 'active' ? 'success' : user.status === 'invited' ? 'pending' : 'warning'}>
                            {user.status || 'active'}
                          </Badge>
                        </button>
                      ) : (
                        <Badge status={user.status === 'active' ? 'success' : user.status === 'invited' ? 'pending' : 'warning'}>
                          {user.status || 'active'}
                        </Badge>
                      )}
                    </td>
                    <td style={tdStyle}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        {canEdit && (
                          <button onClick={() => openEditUser(user)} style={btnBase} title="Edit user">
                            <Edit2 size={12} />
                          </button>
                        )}
                        {canDel && (
                          <button onClick={() => setShowDeleteConfirm({ type: 'user', item: user })}
                            style={btnDanger} title="Delete user">
                            <Trash2 size={12} />
                          </button>
                        )}
                        {!canEdit && !canDel && (
                          <span style={{ fontSize: 11, color: '#a1a1aa' }}>
                            <Eye size={12} style={{ display: 'inline', marginRight: 4 }} />View only
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <Pagination totalItems={filteredUsers.length} page={pgUsers.page} pageSize={pgUsers.pageSize}
            onPageChange={pgUsers.setPage} onPageSizeChange={pgUsers.setPageSize} noun="users" />
        </div>
      )}

      {/* ============ GROUPS TAB ============ */}
      {tab === 'groups' && (
        <div>
          {filteredGroups.length === 0 ? (
            <EmptyState msg={searchTerm ? 'No groups match your search' : 'No groups yet. Create one to get started.'} />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {pgGroups.paginate(filteredGroups).map((group) => {
                const isExpanded = expandedGroup === group.id;
                const members = group.members || [];
                return (
                  <div key={group.id} style={{
                    background: '#fff', border: '1px solid #e4e4e7', borderRadius: 8, overflow: 'hidden',
                  }}>
                    {/* Group Header */}
                    <div
                      onClick={() => setExpandedGroup(isExpanded ? null : group.id)}
                      style={{
                        padding: '16px 20px', cursor: 'pointer',
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={{
                          width: 36, height: 36, borderRadius: 8, background: '#f5f3ff',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          <Shield size={18} style={{ color: '#8b5cf6' }} />
                        </div>
                        <div>
                          <div style={{ fontWeight: 600, color: '#18181b', fontSize: 14 }}>{group.name}</div>
                          <div style={{ fontSize: 12, color: '#71717a' }}>{group.description || 'No description'}</div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <Badge status="info">{members.length} member{members.length !== 1 ? 's' : ''}</Badge>
                        <div style={{ display: 'flex', gap: 6 }}>
                          {canEditGroups && (
                            <button onClick={(e) => { e.stopPropagation(); openEditGroup(group); }}
                              style={btnBase}><Edit2 size={12} /></button>
                          )}
                          {canDeleteGroups && (
                            <button onClick={(e) => { e.stopPropagation(); setShowDeleteConfirm({ type: 'group', item: group }); }}
                              style={btnDanger}><Trash2 size={12} /></button>
                          )}
                        </div>
                        <ChevronRight size={16} style={{
                          color: '#a1a1aa', transition: 'transform 0.2s',
                          transform: isExpanded ? 'rotate(90deg)' : 'rotate(0)',
                        }} />
                      </div>
                    </div>

                    {/* Expanded Members */}
                    {isExpanded && (
                      <div style={{ borderTop: '1px solid #f4f4f5', padding: '16px 20px' }}>
                        {/* Add Member Search */}
                        {canManageMembers && (
                          <div style={{ marginBottom: 16 }}>
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
                              <div style={{ position: 'relative', flex: 1, maxWidth: 360 }}>
                                <UserPlus size={14} style={{ position: 'absolute', left: 10, top: 9, color: '#a1a1aa' }} />
                                <input
                                  placeholder="Search users to add..."
                                  value={showMemberSearch === group.id ? memberSearch : ''}
                                  onFocus={() => setShowMemberSearch(group.id)}
                                  onChange={(e) => { setShowMemberSearch(group.id); setMemberSearch(e.target.value); }}
                                  style={{ ...inputStyle, paddingLeft: 32, fontSize: 12 }}
                                />
                              </div>
                            </div>
                            {/* Search Results Dropdown */}
                            {showMemberSearch === group.id && memberSearch.trim() && (
                              <div style={{
                                border: '1px solid #e4e4e7', borderRadius: 6, background: '#fff',
                                maxHeight: 200, overflowY: 'auto', marginBottom: 12, boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                              }}>
                                {memberSearchResults.length === 0 ? (
                                  <div style={{ padding: 12, fontSize: 12, color: '#a1a1aa', textAlign: 'center' }}>
                                    No matching users found
                                  </div>
                                ) : memberSearchResults.map((u) => (
                                  <div key={u.id} onClick={() => handleAddMember(group.id, u.id)}
                                    style={{
                                      padding: '8px 12px', cursor: 'pointer', display: 'flex',
                                      justifyContent: 'space-between', alignItems: 'center',
                                      borderBottom: '1px solid #f4f4f5', fontSize: 13,
                                    }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                      <Avatar name={u.firstName || u.name} size={24} fontSize={10} />
                                      <span style={{ color: '#18181b' }}>{u.name || u.email}</span>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                      <RoleBadge role={u.role} small />
                                      <Plus size={14} style={{ color: '#0d9488' }} />
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Member List */}
                        {members.length === 0 ? (
                          <div style={{ fontSize: 12, color: '#a1a1aa', padding: '8px 0' }}>
                            No members in this group. {canManageMembers ? 'Use the search above to add users.' : ''}
                          </div>
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            {members.map((member) => {
                              const m = typeof member === 'object' ? member : { id: member, name: member };
                              return (
                                <div key={m.id} style={{
                                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                  padding: '8px 12px', borderRadius: 6, background: '#fafaf9',
                                }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <Avatar name={m.firstName || m.name} size={28} fontSize={11} />
                                    <div>
                                      <div style={{ fontSize: 13, fontWeight: 500, color: '#18181b' }}>
                                        {m.name || m.email || m.id}
                                      </div>
                                      {m.email && <div style={{ fontSize: 11, color: '#a1a1aa' }}>{m.email}</div>}
                                    </div>
                                  </div>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <RoleBadge role={m.role} small />
                                    {canManageMembers && (
                                      <button onClick={() => handleRemoveMember(group.id, m.id)}
                                        style={{ ...btnDanger, padding: '3px 6px' }} title="Remove member">
                                        <UserMinus size={12} />
                                      </button>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          <Pagination totalItems={filteredGroups.length} page={pgGroups.page} pageSize={pgGroups.pageSize}
            onPageChange={pgGroups.setPage} onPageSizeChange={pgGroups.setPageSize} noun="groups" />
        </div>
      )}

      {/* ============ CONNECTOR GROUPS TAB ============ */}
      {tab === 'connectorGroups' && (
        <div>
          {connectorGroups.length === 0 && !searchTerm ? (
            <EmptyState msg="No connector groups yet. Create a group to bundle connectors for agent assignment." />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {pgConnGroups.paginate(connectorGroups
                .filter((g) => !searchTerm || (g.name || '').toLowerCase().includes(searchTerm.toLowerCase()))
              ).map((group) => {
                  const isExpanded = expandedConnGroup === group.id;
                  const memberConnectors = group.connectors || [];
                  const nonMembers = connectors.filter(
                    (c) => !memberConnectors.some((mc) => mc.id === c.id)
                  );

                  return (
                    <div key={group.id} style={{
                      background: '#fff', border: '1px solid #e4e4e7', borderRadius: 8, overflow: 'hidden',
                    }}>
                      {/* Group Header */}
                      <div
                        onClick={() => setExpandedConnGroup(isExpanded ? null : group.id)}
                        style={{
                          padding: '16px 20px', cursor: 'pointer',
                          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <div style={{
                            width: 36, height: 36, borderRadius: 8, background: '#ecfeff',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}>
                            <Layers size={18} color="#06b6d4" />
                          </div>
                          <div>
                            <div style={{ fontWeight: 600, color: '#18181b', fontSize: 14 }}>{group.name}</div>
                            <div style={{ fontSize: 11, color: '#a1a1aa' }}>
                              {memberConnectors.length} connector{memberConnectors.length !== 1 ? 's' : ''}
                              {group.description ? ` · ${group.description}` : ''}
                            </div>
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <button onClick={(e) => { e.stopPropagation(); openEditConnGroup(group); }}
                            style={btnBase}><Edit2 size={12} /></button>
                          <button onClick={(e) => { e.stopPropagation(); handleDeleteConnGroup(group); }}
                            style={btnDanger}><Trash2 size={12} /></button>
                          <ChevronRight size={16} style={{
                            color: '#a1a1aa', transition: 'transform 0.2s',
                            transform: isExpanded ? 'rotate(90deg)' : 'rotate(0)',
                          }} />
                        </div>
                      </div>

                      {/* Expanded Content */}
                      {isExpanded && (
                        <div style={{ borderTop: '1px solid #f4f4f5', padding: '16px 20px' }}>
                          {/* Add Connector to Group */}
                          {nonMembers.length > 0 && (
                            <div style={{ marginBottom: 16 }}>
                              <label style={labelStyle}>Add Connector to Group</label>
                              <div style={{ display: 'flex', gap: 8 }}>
                                <select
                                  id={`add-conn-${group.id}`}
                                  style={{ ...inputStyle, flex: 1 }}
                                  defaultValue=""
                                >
                                  <option value="" disabled>— Select connector —</option>
                                  {nonMembers.map((c) => (
                                    <option key={c.id} value={c.id}>{c.name} ({c.type})</option>
                                  ))}
                                </select>
                                <button
                                  onClick={() => {
                                    const sel = document.getElementById(`add-conn-${group.id}`);
                                    if (sel.value) handleAddConnGroupMember(group.id, sel.value);
                                  }}
                                  style={btnSuccess}
                                >
                                  <Link2 size={12} /> Add
                                </button>
                              </div>
                            </div>
                          )}

                          {/* Member List */}
                          {memberConnectors.length === 0 ? (
                            <div style={{ fontSize: 12, color: '#a1a1aa', padding: '8px 0' }}>
                              No connectors in this group yet. Use the dropdown above to add connectors.
                            </div>
                          ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                              {memberConnectors.map((c) => (
                                <div key={c.id} style={{
                                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                  padding: '10px 12px', borderRadius: 6, background: '#fafaf9',
                                  border: '1px solid #f4f4f5',
                                }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                    <span style={{ fontSize: 16 }}>🔌</span>
                                    <div>
                                      <div style={{ fontSize: 13, fontWeight: 500, color: '#18181b' }}>{c.name}</div>
                                      <div style={{ fontSize: 11, color: '#a1a1aa' }}>
                                        {c.type} · {c.authMethod || 'N/A'} · {c.status || 'unknown'}
                                      </div>
                                    </div>
                                  </div>
                                  <button
                                    onClick={() => handleRemoveConnGroupMember(group.id, c.id)}
                                    style={btnDanger} title="Remove from group"
                                  >
                                    <Unlink size={12} />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>
          )}
          <Pagination totalItems={connectorGroups.filter((g) => !searchTerm || (g.name || '').toLowerCase().includes(searchTerm.toLowerCase())).length}
            page={pgConnGroups.page} pageSize={pgConnGroups.pageSize}
            onPageChange={pgConnGroups.setPage} onPageSizeChange={pgConnGroups.setPageSize} noun="connector groups" />
        </div>
      )}

      {/* ============ ASSIGNMENTS TAB ============ */}
      {tab === 'assignments' && (
        <div>
          {/* ── Assignment Sub-Tabs: Agents | Workflows | Infrastructure ── */}
          <div style={{ display: 'flex', gap: 0, marginBottom: 20, borderBottom: '1px solid #e4e4e7' }}>
            {[
              { id: 'agents', label: 'Agents', icon: <Zap size={13} />, count: agents.length },
              { id: 'workflows', label: 'Workflows', icon: <FileText size={13} />, count: (workflows || []).length },
              { id: 'infrastructure', label: 'Infrastructure', icon: <Database size={13} />, count: (deployments || []).length },
            ].map(st => (
              <button key={st.id} onClick={() => setAssignSubTab(st.id)}
                style={{
                  padding: '10px 18px', border: 'none', background: 'transparent',
                  borderBottom: assignSubTab === st.id ? '2px solid #0d9488' : '2px solid transparent',
                  color: assignSubTab === st.id ? '#0d9488' : '#71717a',
                  fontSize: 13, fontWeight: assignSubTab === st.id ? 600 : 500,
                  cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
                }}>
                {st.icon} {st.label}
                <span style={{
                  fontSize: 10, padding: '1px 6px', borderRadius: 10,
                  background: assignSubTab === st.id ? '#e3f4f1' : '#f4f4f5',
                  color: assignSubTab === st.id ? '#0d9488' : '#71717a',
                }}>{st.count}</span>
              </button>
            ))}
          </div>

          {/* ══════════ AGENTS SUB-TAB ══════════ */}
          {assignSubTab === 'agents' && (
            <div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {pgAssignments.paginate(agents).map(agent => {
                  const isExpanded = expandedAgent === agent.id;
                  const agentAssignments = Array.isArray(assignments) ? assignments.filter(a => a.agentId === agent.id) : [];
                  const agentConnAssignments = Array.isArray(connectorAssignments) ? connectorAssignments.filter(a => a.agentId === agent.id) : [];
                  const agentWfAssigns = (workflows || []).filter(wf => {
                    const wfa = workflowAssignments[wf.id] || [];
                    return wfa.some(a => a.agentId === agent.id);
                  });
                  const agentDeploys = (deployments || []).filter(d => d.agentId === agent.id);
                  const phase = (agent.phase || 'build').toLowerCase();

                  return (
                    <div key={agent.id} style={{ background: '#fff', border: isExpanded ? '2px solid #0d9488' : '1px solid #e4e4e7', borderRadius: 10, overflow: 'hidden' }}>
                      {/* Header */}
                      <div onClick={() => setExpandedAgent(isExpanded ? null : agent.id)}
                        style={{ padding: '14px 18px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12, background: isExpanded ? '#fff5f4' : '#fff' }}>
                        <div style={{ width: 32, height: 32, borderRadius: 8, background: '#e3f4f1', color: '#0d9488', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700, flexShrink: 0 }}>
                          {agent.name?.charAt(0) || 'A'}
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 600, fontSize: 13, color: '#1c1917' }}>{agent.name}</div>
                          <div style={{ fontSize: 11, color: '#a8a29e' }}>{PHASE_MAP[phase] || phase} phase{agent.published ? ' · Published' : ''}{agent.type ? ` · ${agent.type}` : ''}</div>
                        </div>
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                          <Badge useLabel={false} status={agentAssignments.length > 0 ? 'active' : 'inactive'}>{agentAssignments.length} user{agentAssignments.length !== 1 ? 's' : ''}</Badge>
                          {agentConnAssignments.length > 0 && <Badge useLabel={false} status="active">{agentConnAssignments.length} connector{agentConnAssignments.length !== 1 ? 's' : ''}</Badge>}
                          {agentDeploys.length > 0 && <Badge useLabel={false} status="active">{agentDeploys.length} deploy{agentDeploys.length !== 1 ? 's' : ''}</Badge>}
                          {phase === 'act' && agent.published && <Badge useLabel={false} status="active">Live</Badge>}
                          <ChevronRight size={14} style={{ color: '#a8a29e', transform: isExpanded ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }} />
                        </div>
                      </div>

                      {/* ── Expanded: 4-Quadrant Grid ── */}
                      {isExpanded && (
                        <div style={{ borderTop: '2px solid #0d9488', padding: 16 }}>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                            {/* Q1: User / Group Access */}
                            <div style={{ background: '#fafaf9', border: '1px solid #e7e5e4', borderRadius: 8, padding: 14 }}>
                              <div style={{ fontSize: 11, fontWeight: 700, color: '#78716c', textTransform: 'uppercase', letterSpacing: .5, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                                <Users size={12} /> User / Group Access ({agentAssignments.length})
                              </div>
                              {agentAssignments.map((a, i) => {
                                const u = a.userId ? users.find(x => x.id === a.userId) : null;
                                const g = a.groupId ? groups.find(x => x.id === a.groupId) : null;
                                return (
                                  <div key={a.id || i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', borderBottom: '1px solid #e7e5e4', fontSize: 12 }}>
                                    <span style={{ flex: 1, fontWeight: 500 }}>{u ? (u.name || u.email) : g ? g.name : (a.userId || a.groupId)}</span>
                                    <Badge status={a.accessLevel === 'admin' ? 'critical' : a.accessLevel === 'manage' ? 'warning' : 'success'}>{a.accessLevel || 'execute'}</Badge>
                                    {canManageAssignments && <X size={12} style={{ color: '#a8a29e', cursor: 'pointer' }} onClick={() => handleRemoveAssignment(agent.id, a.id)} />}
                                  </div>
                                );
                              })}
                              {agentAssignments.length === 0 && <div style={{ fontSize: 11, color: '#a8a29e', padding: '4px 0' }}>No users assigned</div>}
                              {canManageAssignments && (
                                <button onClick={() => { setAssignForm({ userId: '', groupId: '', accessLevel: 'execute' }); setShowAssignModal(agent.id); }}
                                  style={{ ...btnBase, marginTop: 8, fontSize: 11, border: '1px dashed #d6d3d1', background: 'transparent', color: '#78716c' }}>
                                  <UserPlus size={11} /> Add User / Group
                                </button>
                              )}
                            </div>

                            {/* Q2: Connector Access */}
                            <div style={{ background: '#fafaf9', border: '1px solid #e7e5e4', borderRadius: 8, padding: 14 }}>
                              <div style={{ fontSize: 11, fontWeight: 700, color: '#78716c', textTransform: 'uppercase', letterSpacing: .5, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                                <Link2 size={12} /> Connector Access ({agentConnAssignments.length})
                              </div>
                              {agentConnAssignments.map((a, i) => {
                                const conn = a.connectorId ? connectors.find(c => c.id === a.connectorId) : null;
                                const cGrp = a.connectorGroupId ? connectorGroups.find(g => g.id === a.connectorGroupId) : null;
                                return (
                                  <div key={a.id || i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', borderBottom: '1px solid #e7e5e4', fontSize: 12 }}>
                                    <span style={{ flex: 1, fontWeight: 500 }}>{conn ? conn.name : cGrp ? cGrp.name : '—'}</span>
                                    <Badge status="info">{a.connectorId ? 'connector' : 'group'}</Badge>
                                    {canManageAssignments && <X size={12} style={{ color: '#a8a29e', cursor: 'pointer' }} onClick={() => handleRemoveConnectorAssignment(agent.id, a.id)} />}
                                  </div>
                                );
                              })}
                              {agentConnAssignments.length === 0 && <div style={{ fontSize: 11, color: '#a8a29e', padding: '4px 0' }}>Unrestricted — all connectors</div>}
                              {canManageAssignments && (
                                <button onClick={() => { setConnAssignForm({ connectorId: '', connectorGroupId: '' }); setShowConnAssignModal(agent.id); }}
                                  style={{ ...btnBase, marginTop: 8, fontSize: 11, border: '1px dashed #d6d3d1', background: 'transparent', color: '#78716c' }}>
                                  <Link2 size={11} /> Add Connector
                                </button>
                              )}
                            </div>

                            {/* Q3: Linked Workflows */}
                            <div style={{ background: '#fafaf9', border: '1px solid #e7e5e4', borderRadius: 8, padding: 14 }}>
                              <div style={{ fontSize: 11, fontWeight: 700, color: '#78716c', textTransform: 'uppercase', letterSpacing: .5, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                                <FileText size={12} /> Linked Workflows ({agentWfAssigns.length})
                              </div>
                              {agentWfAssigns.map((wf, i) => (
                                <div key={wf.id || i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', borderBottom: '1px solid #e7e5e4', fontSize: 12 }}>
                                  <span style={{ flex: 1, fontWeight: 500 }}>{wf.name}</span>
                                  <Badge status="active">{wf.status || 'active'}</Badge>
                                </div>
                              ))}
                              {agentWfAssigns.length === 0 && <div style={{ fontSize: 11, color: '#a8a29e', padding: '4px 0' }}>No workflows linked</div>}
                              <div style={{ fontSize: 10, color: '#a8a29e', marginTop: 6, fontStyle: 'italic' }}>Manage in Workflows sub-tab</div>
                            </div>

                            {/* Q4: Deployments */}
                            <div style={{ background: '#fafaf9', border: '1px solid #e7e5e4', borderRadius: 8, padding: 14 }}>
                              <div style={{ fontSize: 11, fontWeight: 700, color: '#78716c', textTransform: 'uppercase', letterSpacing: .5, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                                <Database size={12} /> Deployments ({agentDeploys.length})
                              </div>
                              {agentDeploys.map((d, i) => {
                                const t = (tenants || []).find(x => x.id === d.tenantId);
                                const o = t ? (orgs || []).find(x => x.id === t.orgId) : null;
                                return (
                                  <div key={d.id || i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', borderBottom: '1px solid #e7e5e4', fontSize: 12 }}>
                                    <span style={{ flex: 1, fontWeight: 500 }}>{o ? o.name : '?'} / {t ? t.name : '?'}</span>
                                    <Badge status="info">{d.environment || 'prod'}</Badge>
                                    <Badge useLabel={false} status={d.status === 'RUNNING' || d.status === 'ACTIVE' ? 'active' : d.status === 'FAILED' ? 'critical' : d.status === 'STOPPED' ? 'stopped' : 'pending'}>{d.status}</Badge>
                                  </div>
                                );
                              })}
                              {agentDeploys.length === 0 && <div style={{ fontSize: 11, color: '#a8a29e', padding: '4px 0' }}>Not deployed</div>}
                              <div style={{ fontSize: 10, color: '#a8a29e', marginTop: 6, fontStyle: 'italic' }}>Manage in Infrastructure sub-tab</div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <Pagination totalItems={agents.length} page={pgAssignments.page} pageSize={pgAssignments.pageSize}
                onPageChange={pgAssignments.setPage} onPageSizeChange={pgAssignments.setPageSize} noun="agents" />
            </div>
          )}

          {/* ══════════ WORKFLOWS SUB-TAB ══════════ */}
          {assignSubTab === 'workflows' && (
            <div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {pgWorkflowAssign.paginate(workflows || []).map(wf => {
                  const isExpanded = expandedWorkflow === wf.id;
                  const wfAssigns = workflowAssignments[wf.id] || [];
                  const userAssigns = wfAssigns.filter(a => a.userId || a.groupId);
                  const agentAssigns = wfAssigns.filter(a => a.agentId);

                  return (
                    <div key={wf.id} style={{ background: '#fff', border: isExpanded ? '2px solid #0d9488' : '1px solid #e4e4e7', borderRadius: 10, overflow: 'hidden' }}>
                      <div onClick={() => setExpandedWorkflow(isExpanded ? null : wf.id)}
                        style={{ padding: '14px 18px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12, background: isExpanded ? '#fff5f4' : '#fff' }}>
                        <div style={{ width: 32, height: 32, borderRadius: 8, background: '#f5f5f4', color: '#57534e', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, flexShrink: 0 }}>
                          <FileText size={14} />
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 600, fontSize: 13, color: '#1c1917', display: 'flex', alignItems: 'center', gap: 6 }}>
                            {wf.name}
                            {wf.isSystem && <span style={{ color: '#0d9488', fontSize: 11 }}>★ System</span>}
                          </div>
                          <div style={{ fontSize: 11, color: '#a8a29e' }}>{wf.status || 'active'}{wf.version ? ` · v${wf.version}` : ''}</div>
                        </div>
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                          {userAssigns.length > 0 && <Badge useLabel={false} status="active">{userAssigns.length} user{userAssigns.length !== 1 ? 's' : ''}</Badge>}
                          {agentAssigns.length > 0 && <Badge useLabel={false} status="active">{agentAssigns.length} agent{agentAssigns.length !== 1 ? 's' : ''}</Badge>}
                          {wfAssigns.length === 0 && <Badge useLabel={false} status="inactive">unassigned</Badge>}
                          <ChevronRight size={14} style={{ color: '#a8a29e', transform: isExpanded ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }} />
                        </div>
                      </div>

                      {isExpanded && (
                        <div style={{ borderTop: '2px solid #0d9488', padding: 16 }}>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                            {/* User / Group Access */}
                            <div style={{ background: '#fafaf9', border: '1px solid #e7e5e4', borderRadius: 8, padding: 14 }}>
                              <div style={{ fontSize: 11, fontWeight: 700, color: '#78716c', textTransform: 'uppercase', letterSpacing: .5, marginBottom: 8 }}>
                                User / Group Access ({userAssigns.length})
                              </div>
                              {userAssigns.map((a, i) => {
                                const u = a.userId ? users.find(x => x.id === a.userId) : null;
                                const g = a.groupId ? groups.find(x => x.id === a.groupId) : null;
                                return (
                                  <div key={a.id || i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', borderBottom: '1px solid #e7e5e4', fontSize: 12 }}>
                                    <span style={{ flex: 1, fontWeight: 500 }}>{u ? (u.name || u.email) : g ? g.name : '—'}</span>
                                    <Badge status="success">{a.accessLevel || 'execute'}</Badge>
                                    {canManageAssignments && <X size={12} style={{ color: '#a8a29e', cursor: 'pointer' }} onClick={() => handleRemoveWfAssignment(wf.id, a.id)} />}
                                  </div>
                                );
                              })}
                              {userAssigns.length === 0 && <div style={{ fontSize: 11, color: '#a8a29e' }}>No users assigned</div>}
                              {canManageAssignments && (
                                <button onClick={() => { setWfAssignForm({ userId: '', groupId: '', agentId: '', accessLevel: 'execute' }); setShowWfAssignModal({ workflowId: wf.id, mode: 'userGroup' }); }}
                                  style={{ ...btnBase, marginTop: 8, fontSize: 11, border: '1px dashed #d6d3d1', background: 'transparent', color: '#78716c' }}>
                                  <UserPlus size={11} /> Add User / Group
                                </button>
                              )}
                            </div>
                            {/* Agent Links */}
                            <div style={{ background: '#fafaf9', border: '1px solid #e7e5e4', borderRadius: 8, padding: 14 }}>
                              <div style={{ fontSize: 11, fontWeight: 700, color: '#78716c', textTransform: 'uppercase', letterSpacing: .5, marginBottom: 8 }}>
                                Linked Agents ({agentAssigns.length})
                              </div>
                              {agentAssigns.map((a, i) => {
                                const ag = agents.find(x => x.id === a.agentId);
                                return (
                                  <div key={a.id || i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', borderBottom: '1px solid #e7e5e4', fontSize: 12 }}>
                                    <span style={{ flex: 1, fontWeight: 500 }}>{ag ? ag.name : a.agentId}</span>
                                    <Badge status="info">{a.accessLevel || 'execute'}</Badge>
                                    {canManageAssignments && <X size={12} style={{ color: '#a8a29e', cursor: 'pointer' }} onClick={() => handleRemoveWfAssignment(wf.id, a.id)} />}
                                  </div>
                                );
                              })}
                              {agentAssigns.length === 0 && <div style={{ fontSize: 11, color: '#a8a29e' }}>No agents linked</div>}
                              {canManageAssignments && (
                                <button onClick={() => { setWfAssignForm({ userId: '', groupId: '', agentId: '', accessLevel: 'execute' }); setShowWfAssignModal({ workflowId: wf.id, mode: 'agent' }); }}
                                  style={{ ...btnBase, marginTop: 8, fontSize: 11, border: '1px dashed #d6d3d1', background: 'transparent', color: '#78716c' }}>
                                  <Zap size={11} /> Link Agent
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <Pagination totalItems={(workflows || []).length} page={pgWorkflowAssign.page} pageSize={pgWorkflowAssign.pageSize}
                onPageChange={pgWorkflowAssign.setPage} onPageSizeChange={pgWorkflowAssign.setPageSize} noun="workflows" />
            </div>
          )}

          {/* ══════════ INFRASTRUCTURE SUB-TAB ══════════ */}
          {assignSubTab === 'infrastructure' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <div style={{ fontSize: 13, color: '#57534e' }}>
                  Org → Tenant → Instance (Deployment) hierarchy. Create and manage agent deployments.
                </div>
                {canManageAssignments && (
                  <button onClick={() => { setDeployForm({ agentId: '', orgId: '', tenantId: '', environment: 'production', gatewayUrl: '' }); setShowDeployModal(true); }}
                    style={{ ...btnPrimary, background: '#0d9488' }}>
                    <Plus size={12} /> New Instance
                  </button>
                )}
              </div>

              {infraHierarchy.length === 0 ? (
                <EmptyState msg="No organizations found." />
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {infraHierarchy.map(org => (
                    <div key={org.id} style={{ background: '#fff', border: '1px solid #e4e4e7', borderRadius: 10, overflow: 'hidden' }}>
                      {/* Org Header */}
                      <div onClick={() => setExpandedOrg(expandedOrg === org.id ? null : org.id)}
                        style={{ padding: '14px 18px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10 }}>
                        <Building2 size={16} style={{ color: '#0d9488' }} />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 700, fontSize: 14, color: '#1c1917' }}>{org.name}</div>
                          <div style={{ fontSize: 11, color: '#a8a29e' }}>{org.tenants?.length || 0} tenant{(org.tenants?.length || 0) !== 1 ? 's' : ''} · {org.totalDeployments} deployment{org.totalDeployments !== 1 ? 's' : ''}</div>
                        </div>
                        <ChevronDown size={14} style={{ color: '#a8a29e', transform: expandedOrg === org.id ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }} />
                      </div>

                      {/* Tenants */}
                      {expandedOrg === org.id && (org.tenants || []).map(tenant => (
                        <div key={tenant.id} style={{ borderTop: '1px solid #f4f4f5', marginLeft: 32 }}>
                          <div onClick={() => setExpandedTenant(expandedTenant === tenant.id ? null : tenant.id)}
                            style={{ padding: '10px 18px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}>
                            <Layers size={13} style={{ color: '#78716c' }} />
                            <div style={{ flex: 1 }}>
                              <span style={{ fontWeight: 600, fontSize: 13, color: '#44403c' }}>{tenant.name}</span>
                              <span style={{ fontSize: 11, color: '#a8a29e', marginLeft: 8 }}>{tenant.deployments.length} instance{tenant.deployments.length !== 1 ? 's' : ''}</span>
                            </div>
                            <ChevronRight size={12} style={{ color: '#a8a29e', transform: expandedTenant === tenant.id ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }} />
                          </div>

                          {/* Instances (Deployments) */}
                          {expandedTenant === tenant.id && (
                            <div style={{ marginLeft: 24, paddingBottom: 8 }}>
                              {tenant.deployments.length === 0 && (
                                <div style={{ padding: '8px 18px', fontSize: 11, color: '#a8a29e' }}>No instances. Click "New Instance" to deploy an agent here.</div>
                              )}
                              {tenant.deployments.map(dep => {
                                const depAgent = agents.find(a => a.id === dep.agentId);
                                return (
                                  <div key={dep.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 18px', borderBottom: '1px solid #f4f4f5', fontSize: 12 }}>
                                    <Database size={12} style={{ color: '#a8a29e' }} />
                                    <span style={{ fontWeight: 600, flex: 1 }}>{depAgent ? depAgent.name : dep.agentId?.slice(0, 8)}</span>
                                    <Badge status="info">{dep.environment}</Badge>
                                    <Badge useLabel={false} status={
                                      dep.status === 'RUNNING' || dep.status === 'ACTIVE' ? 'active'
                                        : dep.status === 'FAILED' ? 'critical'
                                        : dep.status === 'STOPPED' ? 'stopped'
                                        : dep.status === 'STARTING' || dep.status === 'UPDATING' ? 'pending'
                                        : 'warning'
                                    }>{dep.status}</Badge>
                                    {dep.version && <span style={{ fontSize: 10, color: '#a8a29e' }}>v{dep.version}</span>}
                                    {canManageAssignments && (
                                      <div style={{ display: 'flex', gap: 4 }}>
                                        {dep.status !== 'RUNNING' && (
                                          <button onClick={() => handleUpdateDeploymentStatus(dep.id, 'RUNNING')} style={{ ...btnBase, fontSize: 10, padding: '2px 6px', color: '#16a34a', border: '1px solid #bbf7d0' }}>Start</button>
                                        )}
                                        {dep.status === 'RUNNING' && (
                                          <button onClick={() => handleUpdateDeploymentStatus(dep.id, 'STOPPED')} style={{ ...btnBase, fontSize: 10, padding: '2px 6px', color: '#d97706', border: '1px solid #fde68a' }}>Stop</button>
                                        )}
                                        <button onClick={() => handleDeleteDeployment(dep.id)} style={{ ...btnBase, fontSize: 10, padding: '2px 6px', color: '#dc2626', border: '1px solid #fecaca' }}>
                                          <Trash2 size={10} />
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Workflow Assignment Modal ── */}
      {showWfAssignModal && (
        <Modal isOpen={true}
          title={showWfAssignModal.mode === 'agent' ? 'Link Agent to Workflow' : 'Add User / Group Access'}
          onClose={() => setShowWfAssignModal(null)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: 8 }}>
            {/* ── User / Group mode ── */}
            {showWfAssignModal.mode === 'userGroup' && (
              <>
                <div>
                  <label style={labelStyle}>Assign To</label>
                  <select style={inputStyle} value={wfAssignForm._type || ''}
                    onChange={e => setWfAssignForm(prev => ({ ...prev, userId: '', groupId: '', _type: e.target.value }))}>
                    <option value="">— Select type —</option>
                    <option value="user">User</option>
                    <option value="group">Group</option>
                  </select>
                </div>
                {wfAssignForm._type === 'user' && (
                  <div>
                    <label style={labelStyle}>User</label>
                    <select style={inputStyle} value={wfAssignForm.userId} onChange={e => setWfAssignForm(prev => ({ ...prev, userId: e.target.value }))}>
                      <option value="">— Select user —</option>
                      {users.map(u => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
                    </select>
                  </div>
                )}
                {wfAssignForm._type === 'group' && (
                  <div>
                    <label style={labelStyle}>Group</label>
                    <select style={inputStyle} value={wfAssignForm.groupId} onChange={e => setWfAssignForm(prev => ({ ...prev, groupId: e.target.value }))}>
                      <option value="">— Select group —</option>
                      {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                    </select>
                  </div>
                )}
              </>
            )}
            {/* ── Agent mode ── */}
            {showWfAssignModal.mode === 'agent' && (
              <div>
                <label style={labelStyle}>Agent</label>
                <select style={inputStyle} value={wfAssignForm.agentId} onChange={e => setWfAssignForm(prev => ({ ...prev, agentId: e.target.value }))}>
                  <option value="">— Select agent —</option>
                  {agents.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
              </div>
            )}
            <div>
              <label style={labelStyle}>Access Level</label>
              <select style={inputStyle} value={wfAssignForm.accessLevel} onChange={e => setWfAssignForm(prev => ({ ...prev, accessLevel: e.target.value }))}>
                {ACCESS_LEVELS.map(l => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
            <button onClick={handleCreateWfAssignment} style={btnPrimary}>
              {showWfAssignModal.mode === 'agent' ? 'Link Agent' : 'Create Assignment'}
            </button>
          </div>
        </Modal>
      )}

      {/* ── Deploy Instance Modal ── */}
      {showDeployModal && (
        <Modal isOpen={true} title="Deploy New Instance" onClose={() => setShowDeployModal(false)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: 8 }}>
            <div>
              <label style={labelStyle}>Agent (Act phase, Published only)</label>
              <select style={inputStyle} value={deployForm.agentId} onChange={e => setDeployForm(prev => ({ ...prev, agentId: e.target.value }))}>
                <option value="">— Select agent —</option>
                {deployableAgents.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Organization</label>
              <select style={inputStyle} value={deployForm.orgId} onChange={e => setDeployForm(prev => ({ ...prev, orgId: e.target.value, tenantId: '' }))}>
                <option value="">— Select org —</option>
                {(orgs || []).map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Tenant</label>
              <select style={inputStyle} value={deployForm.tenantId} onChange={e => setDeployForm(prev => ({ ...prev, tenantId: e.target.value }))}>
                <option value="">— Select tenant —</option>
                {deployFilteredTenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Environment</label>
              <select style={inputStyle} value={deployForm.environment} onChange={e => setDeployForm(prev => ({ ...prev, environment: e.target.value }))}>
                {['production', 'staging', 'uat', 'development'].map(e => <option key={e} value={e}>{e}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Gateway URL (optional)</label>
              <input style={inputStyle} value={deployForm.gatewayUrl} onChange={e => setDeployForm(prev => ({ ...prev, gatewayUrl: e.target.value }))} placeholder="https://..." />
            </div>
            <button onClick={handleCreateDeployment} style={{ ...btnPrimary, background: '#0d9488' }}>Deploy Instance</button>
          </div>
        </Modal>
      )}

      {/* ============ ACCESS REVIEW TAB ============ */}
      {tab === 'accessReview' && (
        <div style={{ display: 'flex', gap: 20 }}>
          {/* ---- Left: User Selector ---- */}
          <div style={{
            width: 260, flexShrink: 0, background: '#fff', border: '1px solid #e4e4e7',
            borderRadius: 8, overflow: 'hidden', alignSelf: 'flex-start',
          }}>
            <div style={{ padding: '14px 16px', borderBottom: '1px solid #e4e4e7', background: '#fafaf9' }}>
              <div style={{ fontWeight: 600, fontSize: 13, color: '#18181b', marginBottom: 8 }}>Select User</div>
              <div style={{ position: 'relative' }}>
                <Search size={13} style={{ position: 'absolute', left: 8, top: 9, color: '#a1a1aa' }} />
                <input placeholder="Search users..." value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ ...inputStyle, paddingLeft: 28, fontSize: 12, padding: '6px 10px 6px 28px' }} />
              </div>
            </div>
            <div style={{ maxHeight: 480, overflowY: 'auto' }}>
              {users
                .filter((u) => {
                  if (!searchTerm) return true;
                  const q = searchTerm.toLowerCase();
                  return (u.name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q)
                    || (u.firstName || '').toLowerCase().includes(q) || (u.lastName || '').toLowerCase().includes(q);
                })
                .map((u) => {
                  const name = u.name || `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email;
                  const selected = reviewUserId === u.id;
                  return (
                    <div key={u.id} onClick={() => handleSelectReviewUser(u.id)}
                      style={{
                        padding: '10px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10,
                        background: selected ? '#e3f4f1' : 'transparent',
                        borderLeft: selected ? '3px solid #0d9488' : '3px solid transparent',
                        borderBottom: '1px solid #f4f4f5',
                      }}>
                      <Avatar name={name} size={28} fontSize={11} />
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ fontSize: 12, fontWeight: selected ? 600 : 500, color: '#18181b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</div>
                        <div style={{ fontSize: 10, color: '#a1a1aa', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.email}</div>
                      </div>
                      <RoleBadge role={u.role} small />
                    </div>
                  );
                })}
            </div>
          </div>

          {/* ---- Right: Review Panels ---- */}
          <div style={{ flex: 1, minWidth: 0 }}>
            {!reviewUserId ? (
              <div style={{
                background: '#fff', border: '1px solid #e4e4e7', borderRadius: 8,
                padding: 64, textAlign: 'center', color: '#a1a1aa',
              }}>
                <ClipboardCheck size={40} style={{ marginBottom: 12, opacity: 0.4 }} />
                <div style={{ fontSize: 15, fontWeight: 600, color: '#71717a', marginBottom: 4 }}>Access Review</div>
                <div style={{ fontSize: 13 }}>Select a user on the left to review their complete access profile.</div>
              </div>
            ) : reviewLoading ? (
              <div style={{
                background: '#fff', border: '1px solid #e4e4e7', borderRadius: 8,
                padding: 48, textAlign: 'center', color: '#a1a1aa', fontSize: 13,
              }}>Loading access data...</div>
            ) : (() => {
              const reviewUser = users.find((u) => u.id === reviewUserId);
              if (!reviewUser) return null;
              const reviewName = reviewUser.name || `${reviewUser.firstName || ''} ${reviewUser.lastName || ''}`.trim() || reviewUser.email;
              const userRole = reviewUser.role || 'Viewer';

              // Build maps
              const orgMap = {};
              orgs.forEach((o) => { orgMap[o.id] = o.name || o.id; });
              const tenantMap = {};
              tenants.forEach((t) => { tenantMap[t.id] = t.name || t.id; });

              // Determine role-based implicit access level. The hierarchy is
              // shared (src/data/roleLevels.js) — this block used to inline its
              // own copy, which drifted twice and silently read any role missing
              // from it as level 0, i.e. "no access at all" on a review screen.
              // The numeric level drives the default agent/workflow access tiers
              // below (admin >= 4, manage >= 3, execute >= 2, read >= 1).
              const roleLevel = levelForRole(userRole);
              const hasRoleAgentAccess = roleLevel >= 1; // All roles have at least agent.read
              const roleAgentLevel = roleLevel >= 4 ? 'admin' : roleLevel >= 3 ? 'manage' : 'execute';
              const hasRoleWorkflowAccess = roleLevel >= 1; // All roles have at least workflow.read
              const roleWorkflowLevel = roleLevel >= 4 ? 'admin' : roleLevel >= 3 ? 'manage' : roleLevel >= 2 ? 'execute' : 'read';

              // Agent access: explicit assignments + implicit role-based
              const userGroupIds = groups.filter((g) => (g.members || []).some((m) => m.userId === reviewUserId || m.id === reviewUserId)).map((g) => g.id);
              const agentAccess = agents.map((agent) => {
                const allAssigns = Array.isArray(assignments) ? assignments.filter((a) => a.agentId === agent.id) : [];
                const directAssign = allAssigns.find((a) => a.userId === reviewUserId);
                const groupAssigns = allAssigns.filter((a) => a.groupId && userGroupIds.includes(a.groupId));
                const isOwner = agent.createdBy === reviewUserId || agent.ownerId === reviewUserId;
                // Determine access source
                const via = directAssign ? 'direct' : groupAssigns.length > 0 ? 'group' : (isOwner && roleLevel === 2) ? 'owner' : hasRoleAgentAccess ? 'role' : null;
                if (!via) return null;
                return {
                  agent,
                  direct: directAssign,
                  groups: groupAssigns,
                  via,
                  isOwner,
                  accessLevel: directAssign?.accessLevel || groupAssigns[0]?.accessLevel || (isOwner ? 'manage' : roleAgentLevel),
                };
              }).filter(Boolean);

              // Workflow access: explicit assignments + implicit role-based
              const explicitWfAccess = reviewWorkflowAssignments.filter((a) =>
                a.userId === reviewUserId || (a.groupId && userGroupIds.includes(a.groupId))
              );
              // Build full workflow access list (explicit + implicit role-based for remaining)
              const explicitWfIds = new Set(explicitWfAccess.map((a) => a.workflowId));
              const implicitWfAccess = hasRoleWorkflowAccess
                ? workflows.filter((wf) => !explicitWfIds.has(wf.id)).map((wf) => ({
                    workflowId: wf.id, workflowName: wf.name, accessLevel: roleWorkflowLevel, via: 'role',
                  }))
                : [];
              const wfAccess = [
                ...explicitWfAccess.map((a) => ({ ...a, via: a.userId === reviewUserId ? 'direct' : 'group' })),
                ...implicitWfAccess,
              ];

              // Connector group memberships
              const userConnGroups = connectorGroups.filter((cg) =>
                (cg.members || []).some((m) => m.userId === reviewUserId || m.id === reviewUserId)
              );

              // Effective permissions from AuthContext ROLE_PERMS
              const ROLE_PERMS_LOCAL = {
                'Super Admin': ['* (all permissions)'],
                'Org Admin': ['org.*', 'tenant.*', 'user.*', 'group.*', 'agent.crud', 'skill.*', 'eval.*', 'execution.*', 'phase.*', 'assignment.*', 'deployment.*', 'inference.*', 'workflow.*', 'connector.*', 'publish.*'],
                'Tenant Admin': ['user.manage/create/edit/toggle', 'group.*', 'agent.edit/read', 'skill.create/edit/push', 'eval.*', 'execution.*', 'phase.read', 'assignment.*', 'deployment.edit/read', 'inference.*', 'workflow.*', 'connector.*', 'publish.read'],
                'Developer': ['agent.own/create/edit/read', 'skill.create/edit/push', 'eval.*', 'execution.read', 'phase.read', 'assignment.read', 'deployment.read', 'inference.read', 'workflow.*', 'connector.*', 'publish.read', 'user.read', 'group.read'],
                'Viewer': ['agent.read', 'eval.read', 'execution.read', 'phase.read', 'assignment.read', 'inference.read', 'workflow.read', 'publish.read', 'user.read', 'group.read'],
              };

              const sectionHeaderStyle = {
                padding: '12px 16px', background: '#fafaf9', borderBottom: '1px solid #e4e4e7',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', userSelect: 'none',
              };
              const renderSectionHeader = (sKey, icon, title, count) => (
                <div onClick={() => setReviewSections((prev) => ({ ...prev, [sKey]: !prev[sKey] }))} style={sectionHeaderStyle}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {icon}
                    <span style={{ fontWeight: 600, fontSize: 13, color: '#18181b' }}>{title}</span>
                    {count !== undefined && (
                      <span style={{ fontSize: 10, padding: '1px 8px', borderRadius: 10, background: '#f4f4f5', color: '#71717a' }}>{count}</span>
                    )}
                  </div>
                  {reviewSections[sKey] ? <ChevronUp size={14} color="#71717a" /> : <ChevronDown size={14} color="#71717a" />}
                </div>
              );

              return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  {/* User Summary Header */}
                  <div style={{
                    background: '#fff', border: '1px solid #e4e4e7', borderRadius: 8,
                    padding: '20px 24px', display: 'flex', alignItems: 'center', gap: 16,
                  }}>
                    <Avatar name={reviewName} size={48} fontSize={18} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 18, fontWeight: 700, color: '#18181b' }}>{reviewName}</div>
                      <div style={{ fontSize: 12, color: '#71717a', marginTop: 2 }}>{reviewUser.email}</div>
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <RoleBadge role={userRole} />
                      <Badge status={reviewUser.status === 'active' ? 'success' : 'warning'} useLabel={false}>
                        {reviewUser.status || 'active'}
                      </Badge>
                    </div>
                  </div>

                  {/* Quick Stats */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
                    {[
                      { label: 'Memberships', value: reviewMemberships.length, icon: <Building2 size={14} />, color: '#8b5cf6' },
                      { label: 'Agent Access', value: agentAccess.length, icon: <Zap size={14} />, color: '#0d9488' },
                      { label: 'Workflow Access', value: wfAccess.length, icon: <Activity size={14} />, color: '#06b6d4' },
                      { label: 'Conn. Groups', value: userConnGroups.length, icon: <Database size={14} />, color: '#57534e' },
                    ].map((s) => (
                      <div key={s.label} style={{
                        background: '#fff', border: '1px solid #e4e4e7', borderRadius: 8,
                        padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 10,
                      }}>
                        <div style={{ width: 32, height: 32, borderRadius: 8, background: `${s.color}10`, color: s.color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{s.icon}</div>
                        <div>
                          <div style={{ fontSize: 18, fontWeight: 700, color: '#18181b' }}>{s.value}</div>
                          <div style={{ fontSize: 10, color: '#71717a' }}>{s.label}</div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* ---- Memberships Section ---- */}
                  <div style={{ background: '#fff', border: '1px solid #e4e4e7', borderRadius: 8, overflow: 'hidden' }}>
                    {renderSectionHeader('memberships', <Building2 size={14} color="#8b5cf6" />, 'Org / Tenant Memberships', reviewMemberships.length)}
                    {reviewSections.memberships && (
                      <div>
                        {reviewMemberships.length === 0 ? (
                          <div style={{ padding: 24, textAlign: 'center', color: '#a1a1aa', fontSize: 12 }}>No memberships found</div>
                        ) : (
                          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                              <tr style={{ borderBottom: '1px solid #e4e4e7' }}>
                                {['Organization', 'Tenant', 'Role', 'Primary', 'Instance'].map((h) => (
                                  <th key={h} style={{ ...thStyle, fontSize: 10 }}>{h}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {reviewMemberships.map((m) => (
                                <tr key={m.id} style={{ borderBottom: '1px solid #f4f4f5' }}>
                                  <td style={{ ...tdStyle, fontSize: 12 }}>{orgMap[m.orgId] || m.orgId || '—'}</td>
                                  <td style={{ ...tdStyle, fontSize: 12 }}>{tenantMap[m.tenantId] || m.tenantId || '—'}</td>
                                  <td style={{ ...tdStyle, fontSize: 12 }}><RoleBadge role={m.role} small /></td>
                                  <td style={{ ...tdStyle, fontSize: 12 }}>
                                    {m.isDefault && <Star size={12} color="#f59e0b" fill="#f59e0b" />}
                                  </td>
                                  <td style={{ ...tdStyle, fontSize: 12, color: '#a1a1aa' }}>{m.instanceId ? (m.instanceId.substring(0, 8) + '...') : '—'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </div>
                    )}
                  </div>

                  {/* ---- Agent Access Section ---- */}
                  <div style={{ background: '#fff', border: '1px solid #e4e4e7', borderRadius: 8, overflow: 'hidden' }}>
                    {renderSectionHeader('agents', <Zap size={14} color="#0d9488" />, 'Agent Access', agentAccess.length)}
                    {reviewSections.agents && (
                      <div>
                        {agentAccess.length === 0 ? (
                          <div style={{ padding: 24, textAlign: 'center', color: '#a1a1aa', fontSize: 12 }}>No agent assignments</div>
                        ) : (<>
                          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                              <tr style={{ borderBottom: '1px solid #e4e4e7' }}>
                                {['Agent', 'Phase', 'Access Level', 'Via', 'Published'].map((h) => (
                                  <th key={h} style={{ ...thStyle, fontSize: 10 }}>{h}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {pgReviewAgents.paginate(agentAccess).map((aa) => {
                                const phase = (aa.agent.phase || 'build').toLowerCase();
                                const viaColors = { direct: '#059669', group: '#8b5cf6', role: '#d97706', owner: '#0ea5e9' };
                                const viaIcons = { direct: <Eye size={10} />, group: <Users size={10} />, role: <Shield size={10} />, owner: <Star size={10} /> };
                                const viaLabels = {
                                  direct: 'Direct',
                                  group: (aa.groups || []).map((g) => groups.find((gg) => gg.id === g.groupId)?.name || 'Group').join(', '),
                                  role: `${userRole}`,
                                  owner: 'Owner',
                                };
                                return (
                                  <tr key={aa.agent.id} style={{ borderBottom: '1px solid #f4f4f5' }}>
                                    <td style={{ ...tdStyle, fontSize: 12 }}>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <span style={{ fontSize: 14 }}>{'\u{1F916}'}</span>
                                        <span style={{ fontWeight: 500 }}>{aa.agent.name}</span>
                                      </div>
                                    </td>
                                    <td style={{ ...tdStyle, fontSize: 12 }}>
                                      <Badge status={phase === 'act' ? 'success' : phase === 'decide' ? 'warning' : 'info'} useLabel={false}>
                                        {PHASE_MAP[phase] || phase}
                                      </Badge>
                                    </td>
                                    <td style={{ ...tdStyle, fontSize: 12 }}>
                                      <span style={{
                                        padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600,
                                        background: aa.accessLevel === 'admin' ? '#fef2f2' : aa.accessLevel === 'manage' ? '#e3f4f1' : '#f5f5f4',
                                        color: aa.accessLevel === 'admin' ? '#991b1b' : aa.accessLevel === 'manage' ? '#0a766b' : '#57534e',
                                      }}>{aa.accessLevel}</span>
                                    </td>
                                    <td style={{ ...tdStyle, fontSize: 12 }}>
                                      <span style={{ fontSize: 11, color: viaColors[aa.via] || '#71717a', display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                                        {viaIcons[aa.via]} {viaLabels[aa.via] || aa.via}
                                      </span>
                                    </td>
                                    <td style={{ ...tdStyle, fontSize: 12 }}>
                                      {aa.agent.published ? <Check size={14} color="#059669" /> : <span style={{ color: '#d4d4d8' }}>—</span>}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                          <Pagination totalItems={agentAccess.length} page={pgReviewAgents.page} pageSize={pgReviewAgents.pageSize}
                            onPageChange={pgReviewAgents.setPage} onPageSizeChange={pgReviewAgents.setPageSize} noun="agents" />
                        </>)}
                      </div>
                    )}
                  </div>

                  {/* ---- Workflow Access Section ---- */}
                  <div style={{ background: '#fff', border: '1px solid #e4e4e7', borderRadius: 8, overflow: 'hidden' }}>
                    {renderSectionHeader('workflows', <Activity size={14} color="#06b6d4" />, 'Workflow Access', wfAccess.length)}
                    {reviewSections.workflows && (
                      <div>
                        {wfAccess.length === 0 ? (
                          <div style={{ padding: 24, textAlign: 'center', color: '#a1a1aa', fontSize: 12 }}>No workflow assignments</div>
                        ) : (<>
                          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                              <tr style={{ borderBottom: '1px solid #e4e4e7' }}>
                                {['Workflow', 'Access Level', 'Via'].map((h) => (
                                  <th key={h} style={{ ...thStyle, fontSize: 10 }}>{h}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {pgReviewWorkflows.paginate(wfAccess).map((wa, idx) => {
                                const wVia = wa.via || (wa.userId === reviewUserId ? 'direct' : 'group');
                                const wViaColors = { direct: '#059669', group: '#8b5cf6', role: '#d97706' };
                                const wViaIcons = { direct: <Eye size={10} />, group: <Users size={10} />, role: <Shield size={10} /> };
                                const wViaLabel = wVia === 'direct' ? 'Direct' : wVia === 'role' ? userRole :
                                  (groups.find((g) => g.id === wa.groupId)?.name || 'Group');
                                return (
                                  <tr key={`${wa.workflowId}-${idx}`} style={{ borderBottom: '1px solid #f4f4f5' }}>
                                    <td style={{ ...tdStyle, fontSize: 12 }}>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <Activity size={12} color="#06b6d4" />
                                        <span style={{ fontWeight: 500 }}>{wa.workflowName || wa.workflowId}</span>
                                      </div>
                                    </td>
                                    <td style={{ ...tdStyle, fontSize: 12 }}>
                                      <span style={{
                                        padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600,
                                        background: '#ecfeff', color: '#0e7490',
                                      }}>{wa.accessLevel || 'execute'}</span>
                                    </td>
                                    <td style={{ ...tdStyle, fontSize: 12 }}>
                                      <span style={{ fontSize: 11, color: wViaColors[wVia] || '#71717a', display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                                        {wViaIcons[wVia]} {wViaLabel}
                                      </span>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                          <Pagination totalItems={wfAccess.length} page={pgReviewWorkflows.page} pageSize={pgReviewWorkflows.pageSize}
                            onPageChange={pgReviewWorkflows.setPage} onPageSizeChange={pgReviewWorkflows.setPageSize} noun="workflows" />
                        </>)}
                      </div>
                    )}
                  </div>

                  {/* ---- Connector Groups Section ---- */}
                  <div style={{ background: '#fff', border: '1px solid #e4e4e7', borderRadius: 8, overflow: 'hidden' }}>
                    {renderSectionHeader('connectors', <Database size={14} color="#57534e" />, 'Connector Group Memberships', userConnGroups.length)}
                    {reviewSections.connectors && (
                      <div>
                        {userConnGroups.length === 0 ? (
                          <div style={{ padding: 24, textAlign: 'center', color: '#a1a1aa', fontSize: 12 }}>Not a member of any connector groups</div>
                        ) : (
                          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                              <tr style={{ borderBottom: '1px solid #e4e4e7' }}>
                                {['Connector Group', 'Description', 'Members', 'Connectors'].map((h) => (
                                  <th key={h} style={{ ...thStyle, fontSize: 10 }}>{h}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {userConnGroups.map((cg) => {
                                const cgConnectors = Array.isArray(connectorAssignments)
                                  ? connectorAssignments.filter((ca) => ca.connectorGroupId === cg.id) : [];
                                return (
                                  <tr key={cg.id} style={{ borderBottom: '1px solid #f4f4f5' }}>
                                    <td style={{ ...tdStyle, fontSize: 12, fontWeight: 500 }}>{cg.name}</td>
                                    <td style={{ ...tdStyle, fontSize: 12, color: '#71717a' }}>{cg.description || '—'}</td>
                                    <td style={{ ...tdStyle, fontSize: 12 }}>{(cg.members || []).length}</td>
                                    <td style={{ ...tdStyle, fontSize: 12 }}>{cgConnectors.length}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        )}
                      </div>
                    )}
                  </div>

                  {/* ---- Effective Permissions Section ---- */}
                  <div style={{ background: '#fff', border: '1px solid #e4e4e7', borderRadius: 8, overflow: 'hidden' }}>
                    {renderSectionHeader('permissions', <Lock size={14} color="#d97706" />, 'Effective Permissions')}
                    {reviewSections.permissions && (
                      <div style={{ padding: 16 }}>
                        <div style={{ fontSize: 11, color: '#71717a', marginBottom: 12 }}>
                          Based on role: <RoleBadge role={userRole} small />
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                          {(ROLE_PERMS_LOCAL[userRole] || []).map((p, i) => (
                            <span key={i} style={{
                              display: 'inline-block', padding: '3px 10px', borderRadius: 4,
                              fontSize: 11, fontFamily: 'monospace',
                              background: p.startsWith('*') ? '#fef2f2' : '#f4f4f5',
                              color: p.startsWith('*') ? '#991b1b' : '#52525b',
                              border: `1px solid ${p.startsWith('*') ? '#fecaca' : '#e4e4e7'}`,
                            }}>{p}</span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* ========== ESCALATION CHAINS TAB ========== */}
      {tab === 'escalation' && (
        <div>
          {escLoading ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#a1a1aa' }}>Loading escalation policies…</div>
          ) : escPolicies.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center' }}>
              <AlertTriangle size={28} color="#d97706" />
              <div style={{ marginTop: 8, fontSize: 14, fontWeight: 600, color: '#18181b' }}>No Escalation Policies</div>
              <div style={{ fontSize: 12, color: '#71717a', marginTop: 4, maxWidth: 420, margin: '4px auto 16px' }}>
                Define escalation chains to route remediation tasks and approval requests to the right
                people at each tier. Import from an enterprise IGA system (SailPoint, Saviynt) or create manually.
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
                <button onClick={() => { setEditingEscPolicy(null); setEscForm({ name: '', description: '', scope: 'DEFAULT', scopeFilter: '', tiers: [{ tier: 1, name: '', targetType: 'ROLE', targetValue: 'Org Admin', slaHours: 24 }] }); setShowEscModal(true); }} style={btnPrimary}>
                  <Plus size={13} /> Create Policy
                </button>
                <button onClick={() => { document.getElementById('esc-import-input')?.click(); }} style={{ ...btnBase, border: '1px solid #d97706', color: '#d97706', fontWeight: 600 }}>
                  <FileText size={13} /> Import from Excel
                </button>
                <button onClick={() => { setIgaSource(''); setIgaConnectorId(''); setShowIgaModal(true); }} style={{ ...btnBase, border: '1px solid #7c3aed', color: '#7c3aed', fontWeight: 600 }}>
                  <Link2 size={13} /> Sync from IGA
                </button>
                <input id="esc-import-input" type="file" accept=".xlsx,.xls,.csv" style={{ display: 'none' }} onChange={async (e) => {
                  const file = e.target.files?.[0]; if (!file) return;
                  addToast('Excel import requires backend parsing — upload the file via the import API', 'info');
                  e.target.value = '';
                }} />
              </div>
            </div>
          ) : (
            <div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginBottom: 12 }}>
                <button onClick={() => { setIgaSource(''); setIgaConnectorId(''); setShowIgaModal(true); }} style={{ ...btnBase, border: '1px solid #7c3aed', color: '#7c3aed', fontWeight: 600 }}>
                  <Link2 size={13} /> Sync from IGA
                </button>
                <button onClick={() => { document.getElementById('esc-import-input2')?.click(); }} style={{ ...btnBase, border: '1px solid #d97706', color: '#d97706', fontWeight: 600 }}>
                  <FileText size={13} /> Import from Excel
                </button>
                <input id="esc-import-input2" type="file" accept=".xlsx,.xls,.csv" style={{ display: 'none' }} onChange={async (e) => {
                  const file = e.target.files?.[0]; if (!file) return;
                  addToast('Excel import requires backend parsing — upload the file via the import API', 'info');
                  e.target.value = '';
                }} />
                <button onClick={() => { setEditingEscPolicy(null); setEscForm({ name: '', description: '', scope: 'DEFAULT', scopeFilter: '', tiers: [{ tier: 1, name: '', targetType: 'ROLE', targetValue: 'Org Admin', slaHours: 24 }] }); setShowEscModal(true); }} style={btnPrimary}>
                  <Plus size={13} /> Create Policy
                </button>
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff', borderRadius: 8, border: '1px solid #e4e4e7', overflow: 'hidden' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #e4e4e7', background: '#fafafa' }}>
                    {['Policy Name', 'Scope', 'Source', 'Tiers', 'Status', 'Created', 'Actions'].map(h => (
                      <th key={h} style={thStyle}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {escPolicies.map(policy => (
                    <tr key={policy.id} style={{ borderBottom: '1px solid #f4f4f5' }}>
                      <td style={{ ...tdStyle, fontWeight: 600 }}>
                        {policy.name}
                        {policy.description && <div style={{ fontSize: 11, color: '#71717a', fontWeight: 400 }}>{policy.description}</div>}
                      </td>
                      <td style={tdStyle}>
                        <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10, background: policy.scope === 'DEFAULT' ? '#dbeafe' : policy.scope === 'PRIORITY' ? '#fef3c7' : '#e0e7ff', color: policy.scope === 'DEFAULT' ? '#1e40af' : policy.scope === 'PRIORITY' ? '#92400e' : '#3730a3' }}>
                          {policy.scope}
                        </span>
                        {policy.scopeFilter && <span style={{ fontSize: 11, color: '#71717a', marginLeft: 4 }}>({policy.scopeFilter})</span>}
                      </td>
                      <td style={tdStyle}>
                        <span style={{ fontSize: 11, fontWeight: 500, color: policy.source === 'MANUAL' ? '#52525b' : policy.source === 'EXCEL_IMPORT' ? '#059669' : '#7c3aed' }}>
                          {policy.source === 'MANUAL' ? 'Manual' : policy.source === 'EXCEL_IMPORT' ? 'Excel Import' : 'IGA Sync'}
                        </span>
                      </td>
                      <td style={{ ...tdStyle, fontWeight: 600 }}>{policy.tierCount ?? (policy.tiers ? (Array.isArray(policy.tiers) ? policy.tiers.length : 0) : 0)}</td>
                      <td style={tdStyle}>
                        <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10, background: policy.isActive ? '#dcfce7' : '#fee2e2', color: policy.isActive ? '#166534' : '#991b1b' }}>
                          {policy.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td style={{ ...tdStyle, fontSize: 11, color: '#71717a' }}>
                        {policy.createdAt ? new Date(policy.createdAt).toLocaleDateString() : '—'}
                        {policy.createdByName && <div>{policy.createdByName}</div>}
                      </td>
                      <td style={tdStyle}>
                        <div style={{ display: 'flex', gap: 4 }}>
                          <button onClick={() => {
                            setEditingEscPolicy(policy);
                            const tiers = Array.isArray(policy.tiers) ? policy.tiers : [];
                            setEscForm({ name: policy.name, description: policy.description || '', scope: policy.scope, scopeFilter: policy.scopeFilter || '', tiers: tiers.length > 0 ? tiers : [{ tier: 1, name: '', targetType: 'ROLE', targetValue: 'Org Admin', slaHours: 24 }] });
                            setShowEscModal(true);
                          }} style={btnBase} title="Edit"><Edit2 size={12} /></button>
                          <button onClick={async () => {
                            if (!window.confirm(`Deactivate escalation policy "${policy.name}"?`)) return;
                            try {
                              await api.ccDeleteEscalationPolicy(policy.id);
                              addToast('Policy deactivated', 'success');
                              fetchEscPolicies();
                            } catch (_) { addToast('Failed to deactivate', 'error'); }
                          }} style={btnDanger} title="Deactivate"><Trash2 size={12} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Escalation Policy Create/Edit Modal ── */}
      {showEscModal && (
        <Modal isOpen={showEscModal} title={editingEscPolicy ? `Edit: ${editingEscPolicy.name}` : 'Create Escalation Policy'}
          onClose={() => setShowEscModal(false)}
          actions={<>
            <button onClick={() => setShowEscModal(false)} style={btnBase}>Cancel</button>
            <button onClick={async () => {
              try {
                const tiersJson = escForm.tiers.map((t, i) => ({ ...t, tier: i + 1 }));
                const payload = { name: escForm.name, description: escForm.description, scope: escForm.scope, scopeFilter: escForm.scopeFilter || null, tiers: tiersJson };
                let res;
                if (editingEscPolicy) {
                  res = await api.ccUpdateEscalationPolicy(editingEscPolicy.id, payload);
                } else {
                  res = await api.ccCreateEscalationPolicy(payload);
                }
                if (res.ok) {
                  addToast(editingEscPolicy ? 'Policy updated' : 'Policy created', 'success');
                  setShowEscModal(false);
                  fetchEscPolicies();
                } else {
                  const err = await res.json().catch(() => ({}));
                  addToast(err.message || 'Failed to save policy', 'error');
                }
              } catch (_) { addToast('Error saving policy', 'error'); }
            }} style={btnPrimary}>{editingEscPolicy ? 'Save Changes' : 'Create Policy'}</button>
          </>}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label style={labelStyle}>Policy Name *</label>
                <input value={escForm.name} onChange={e => setEscForm(f => ({ ...f, name: e.target.value }))} style={inputStyle} placeholder="e.g., Default Escalation Chain" />
              </div>
              <div>
                <label style={labelStyle}>Scope</label>
                <select value={escForm.scope} onChange={e => setEscForm(f => ({ ...f, scope: e.target.value }))} style={inputStyle}>
                  <option value="DEFAULT">Default (org-wide fallback)</option>
                  <option value="PRIORITY">Priority-specific</option>
                  <option value="FRAMEWORK">Framework-specific</option>
                  <option value="CUSTOM">Custom (manual assignment)</option>
                </select>
              </div>
            </div>
            {(escForm.scope === 'PRIORITY' || escForm.scope === 'FRAMEWORK') && (
              <div>
                <label style={labelStyle}>{escForm.scope === 'PRIORITY' ? 'Priority Level' : 'Framework ID'}</label>
                {escForm.scope === 'PRIORITY' ? (
                  <select value={escForm.scopeFilter} onChange={e => setEscForm(f => ({ ...f, scopeFilter: e.target.value }))} style={inputStyle}>
                    <option value="">Select priority…</option>
                    <option value="CRITICAL">Critical</option>
                    <option value="HIGH">High</option>
                    <option value="MEDIUM">Medium</option>
                    <option value="LOW">Low</option>
                  </select>
                ) : (
                  <input value={escForm.scopeFilter} onChange={e => setEscForm(f => ({ ...f, scopeFilter: e.target.value }))} style={inputStyle} placeholder="Framework UUID" />
                )}
              </div>
            )}
            <div>
              <label style={labelStyle}>Description</label>
              <textarea value={escForm.description} onChange={e => setEscForm(f => ({ ...f, description: e.target.value }))} style={{ ...inputStyle, resize: 'vertical' }} rows={2} placeholder="Optional description…" />
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <label style={{ ...labelStyle, marginBottom: 0 }}>Escalation Tiers</label>
                <button onClick={() => setEscForm(f => ({ ...f, tiers: [...f.tiers, { tier: f.tiers.length + 1, name: '', targetType: 'ROLE', targetValue: 'Org Admin', slaHours: 24 }] }))} style={{ ...btnBase, fontSize: 11 }}>
                  <Plus size={11} /> Add Tier
                </button>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {escForm.tiers.map((tier, idx) => (
                  <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', background: '#fafafa', borderRadius: 8, border: '1px solid #e4e4e7' }}>
                    <span style={{ width: 22, height: 22, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#18181b', color: '#fff', fontSize: 10, fontWeight: 700, flexShrink: 0 }}>{idx + 1}</span>
                    <input value={tier.name} onChange={e => { const t = [...escForm.tiers]; t[idx] = { ...t[idx], name: e.target.value }; setEscForm(f => ({ ...f, tiers: t })); }} style={{ ...inputStyle, flex: 1 }} placeholder="Tier name (e.g., Team Lead)" />
                    <select value={tier.targetType} onChange={e => { const t = [...escForm.tiers]; t[idx] = { ...t[idx], targetType: e.target.value }; setEscForm(f => ({ ...f, tiers: t })); }} style={{ ...inputStyle, width: 100 }}>
                      <option value="ROLE">Role</option>
                      <option value="USER">User</option>
                      <option value="GROUP">Group</option>
                    </select>
                    {tier.targetType === 'ROLE' ? (
                      <select value={tier.targetValue} onChange={e => { const t = [...escForm.tiers]; t[idx] = { ...t[idx], targetValue: e.target.value }; setEscForm(f => ({ ...f, tiers: t })); }} style={{ ...inputStyle, width: 130 }}>
                        {ROLE_OPTIONS.map(r => <option key={r} value={r}>{r}</option>)}
                      </select>
                    ) : (
                      <input value={tier.targetValue} onChange={e => { const t = [...escForm.tiers]; t[idx] = { ...t[idx], targetValue: e.target.value }; setEscForm(f => ({ ...f, tiers: t })); }} style={{ ...inputStyle, width: 130 }} placeholder={tier.targetType === 'USER' ? 'User UUID' : 'Group UUID'} />
                    )}
                    <input type="number" value={tier.slaHours} onChange={e => { const t = [...escForm.tiers]; t[idx] = { ...t[idx], slaHours: parseInt(e.target.value) || 0 }; setEscForm(f => ({ ...f, tiers: t })); }} style={{ ...inputStyle, width: 60, textAlign: 'center' }} title="SLA hours" />
                    <span style={{ fontSize: 10, color: '#71717a', whiteSpace: 'nowrap' }}>hrs</span>
                    {escForm.tiers.length > 1 && (
                      <button onClick={() => setEscForm(f => ({ ...f, tiers: f.tiers.filter((_, i) => i !== idx) }))} style={{ ...btnDanger, padding: '4px 6px' }}>
                        <X size={11} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Modal>
      )}

      {/* ── IGA Sync Modal ── */}
      {showIgaModal && (
        <Modal isOpen={showIgaModal} title="Sync Escalation Chains from IGA"
          onClose={() => setShowIgaModal(false)}
          actions={<>
            <button onClick={() => setShowIgaModal(false)} style={btnBase}>Cancel</button>
            <button disabled={igaSyncing || !igaSource} onClick={async () => {
              try {
                setIgaSyncing(true);
                let res;
                if (igaSyncDirection === 'OUTBOUND' && igaConnectorId) {
                  // Outbound: we pull from the IGA's SCIM /Users endpoint
                  res = await api.ccTriggerScimSync(igaConnectorId);
                } else {
                  // Inbound / default: IGA pushes or we use the standard iga-sync endpoint
                  res = await api.ccSyncEscalationPoliciesFromIga({
                    igaSource: igaSource,
                    connectorId: igaConnectorId || undefined,
                    policies: [] // Backend will pull from the connector
                  });
                }
                if (res.ok) {
                  const data = await res.json();
                  const count = data.syncedCount || data.created || 0;
                  const chains = data.escalationChainsGenerated || 0;
                  addToast(`Synced ${count} user(s), generated ${chains} escalation chain(s) from ${igaSource}`, 'success');
                  setShowIgaModal(false);
                  fetchEscPolicies();
                } else {
                  const err = await res.json().catch(() => ({}));
                  addToast(err.message || 'IGA sync failed', 'error');
                }
              } catch (e) {
                addToast('IGA sync failed: ' + (e.message || 'Unknown error'), 'error');
              } finally { setIgaSyncing(false); }
            }} style={{ ...btnPrimary, background: '#7c3aed', opacity: (!igaSource || igaSyncing) ? 0.5 : 1 }}>
              {igaSyncing ? 'Syncing…' : 'Start Sync'}
            </button>
          </>}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ padding: '12px 16px', background: '#f5f3ff', borderRadius: 8, border: '1px solid #ddd6fe' }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#5b21b6', marginBottom: 4 }}>How SCIM 2.0 IGA Sync Works</div>
              <div style={{ fontSize: 12, color: '#6d28d9', lineHeight: 1.5 }}>
                Agentlets supports standard SCIM 2.0 (RFC 7643/7644) for syncing organizational hierarchies from any
                IGA system. Manager chains are automatically converted to escalation tiers. Choose <strong>Inbound</strong> to
                let your IGA push via our SCIM endpoint, or <strong>Outbound</strong> to pull from your IGA's SCIM endpoint.
              </div>
            </div>

            <div>
              <label style={labelStyle}>IGA System *</label>
              <select value={igaSource} onChange={e => setIgaSource(e.target.value)} style={inputStyle}>
                <option value="">Select IGA system…</option>
                <option value="SAILPOINT">SailPoint IdentityIQ / IdentityNow</option>
                <option value="SAVIYNT">Saviynt Enterprise Identity Cloud</option>
                <option value="CYBERARK">CyberArk Identity</option>
                <option value="OKTA">Okta Identity Governance</option>
                <option value="ONE_IDENTITY">One Identity Manager</option>
                <option value="CUSTOM">Custom IGA Provider</option>
              </select>
            </div>

            {/* Sync Direction */}
            {igaSource && (
              <div>
                <label style={labelStyle}>Sync Direction *</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  {[
                    { val: 'INBOUND', label: 'Inbound (IGA → Agentlets)', desc: 'IGA pushes users via our SCIM /Users endpoint' },
                    { val: 'OUTBOUND', label: 'Outbound (Agentlets ← IGA)', desc: 'We pull users from your IGA\'s SCIM endpoint' },
                    { val: 'BIDIRECTIONAL', label: 'Bidirectional', desc: 'Both inbound provisioning + outbound sync' },
                  ].map(dir => (
                    <button key={dir.val} onClick={() => setIgaSyncDirection(dir.val)}
                      title={dir.desc}
                      style={{
                        flex: 1, padding: '10px 8px', borderRadius: 8, border: `2px solid ${igaSyncDirection === dir.val ? '#7c3aed' : '#e4e4e7'}`,
                        background: igaSyncDirection === dir.val ? '#f5f3ff' : '#fff', cursor: 'pointer',
                        fontSize: 11, fontWeight: igaSyncDirection === dir.val ? 600 : 400,
                        color: igaSyncDirection === dir.val ? '#5b21b6' : '#71717a', textAlign: 'center',
                      }}>
                      {dir.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Inbound SCIM Provisioning URL */}
            {igaSource && (igaSyncDirection === 'INBOUND' || igaSyncDirection === 'BIDIRECTIONAL') && (
              <div style={{ padding: '12px 16px', background: '#ecfdf5', borderRadius: 8, border: '1px solid #a7f3d0' }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#065f46', marginBottom: 6 }}>SCIM 2.0 Provisioning Endpoint</div>
                <div style={{ fontSize: 11, color: '#047857', marginBottom: 8, lineHeight: 1.5 }}>
                  Configure your IGA system to provision users to this SCIM endpoint. The Bearer token is generated
                  when you configure the connector. User creates/updates will automatically maintain manager hierarchy
                  and regenerate escalation chains.
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <code style={{
                    flex: 1, padding: '8px 12px', background: '#fff', borderRadius: 6, border: '1px solid #d1fae5',
                    fontSize: 12, fontFamily: 'monospace', color: '#065f46', wordBreak: 'break-all',
                  }}>
                    {window.location.origin}/scim/v2/Users
                  </code>
                  <button onClick={() => {
                    navigator.clipboard.writeText(`${window.location.origin}/scim/v2/Users`);
                    setScimEndpointCopied(true);
                    setTimeout(() => setScimEndpointCopied(false), 2000);
                  }} style={{ ...btnBase, padding: '6px 10px', minWidth: 'auto', fontSize: 11 }}>
                    {scimEndpointCopied ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy</>}
                  </button>
                </div>
                <div style={{ fontSize: 10, color: '#059669', marginTop: 6 }}>
                  Supports: GET, POST, PUT, PATCH, DELETE &nbsp;·&nbsp; Auth: Bearer token &nbsp;·&nbsp; RFC 7644 compliant
                </div>
                <div style={{ fontSize: 10, color: '#059669', marginTop: 2 }}>
                  Discovery: <code style={{ background: '#d1fae5', padding: '1px 4px', borderRadius: 3 }}>/scim/v2/ServiceProviderConfig</code>&nbsp;
                  <code style={{ background: '#d1fae5', padding: '1px 4px', borderRadius: 3 }}>/scim/v2/Schemas</code>&nbsp;
                  <code style={{ background: '#d1fae5', padding: '1px 4px', borderRadius: 3 }}>/scim/v2/ResourceTypes</code>
                </div>
              </div>
            )}

            {igaConnectors.length > 0 ? (
              <div>
                <label style={labelStyle}>Connected IGA Connector</label>
                <select value={igaConnectorId} onChange={e => setIgaConnectorId(e.target.value)} style={inputStyle}>
                  <option value="">Select connector…</option>
                  {igaConnectors.map(c => (
                    <option key={c.id} value={c.id}>{c.name} ({c.connectorType || c.type})</option>
                  ))}
                </select>
                <div style={{ fontSize: 11, color: '#059669', marginTop: 4 }}>
                  <Check size={10} style={{ display: 'inline', marginRight: 3 }} />
                  {igaConnectors.length} IGA connector{igaConnectors.length !== 1 ? 's' : ''} detected
                </div>
              </div>
            ) : (
              <div style={{ padding: '12px 16px', background: '#fef3c7', borderRadius: 8, border: '1px solid #fde68a' }}>
                <div style={{ fontSize: 12, color: '#92400e' }}>
                  <AlertTriangle size={12} style={{ display: 'inline', marginRight: 4, verticalAlign: -2 }} />
                  No IGA connectors detected. Go to <strong>Connectors</strong> to add your IGA system first, then return here to sync.
                  {igaSyncDirection === 'INBOUND' || igaSyncDirection === 'BIDIRECTIONAL' ? (
                    <span> For inbound SCIM provisioning, configure the connector with a SCIM bearer token first.</span>
                  ) : (
                    <span> You can also trigger the sync programmatically via the <code style={{ background: '#fde68a', padding: '1px 4px', borderRadius: 3 }}>POST /escalation-policies/scim-sync/{'{connectorId}'}</code> API.</span>
                  )}
                </div>
              </div>
            )}

            {igaSource && (
              <div style={{ padding: '10px 14px', background: '#fafafa', borderRadius: 8, border: '1px solid #e4e4e7' }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#18181b', marginBottom: 4 }}>
                  {igaSyncDirection === 'INBOUND' ? 'Inbound Provisioning' : igaSyncDirection === 'OUTBOUND' ? 'Outbound Sync' : 'Bidirectional Sync'} — {igaSource}
                </div>
                <div style={{ fontSize: 11, color: '#71717a', lineHeight: 1.6 }}>
                  {igaSyncDirection === 'INBOUND' || igaSyncDirection === 'BIDIRECTIONAL' ? (
                    <>Your IGA system pushes user provisioning changes (create, update, deactivate) via SCIM 2.0
                    to our <code style={{ background: '#e4e4e7', padding: '1px 4px', borderRadius: 3 }}>/scim/v2/Users</code> endpoint.
                    Manager relationships from the Enterprise User extension (<code style={{ background: '#e4e4e7', padding: '1px 4px', borderRadius: 3 }}>manager.value</code>) are
                    automatically walked to build escalation chains.</>
                  ) : (
                    <>
                    {igaSource === 'SAILPOINT' && 'SailPoint sync pulls Identity Cubes via SCIM /Users → resolves manager hierarchy → maps to escalation tiers.'}
                    {igaSource === 'SAVIYNT' && 'Saviynt sync pulls user hierarchy via SCIM /Users → resolves manager chain → maps to escalation tiers.'}
                    {igaSource === 'CYBERARK' && 'CyberArk sync pulls identities via SCIM /Users → resolves manager relationships → maps to escalation tiers.'}
                    {igaSource === 'OKTA' && 'Okta sync pulls users via SCIM /Users → resolves manager relationships → maps organizational structure to escalation tiers.'}
                    {igaSource === 'ONE_IDENTITY' && 'One Identity sync pulls users via SCIM /Users → resolves manager chains → maps hierarchy to escalation tiers.'}
                    {igaSource === 'CUSTOM' && 'Custom IGA sync pulls from your SCIM /Users endpoint. Ensure the Enterprise User extension includes manager.value for hierarchy resolution.'}
                    </>
                  )}
                </div>
                <div style={{ fontSize: 11, color: '#3b82f6', marginTop: 6 }}>
                  <Clock size={10} style={{ display: 'inline', marginRight: 3 }} />
                  After initial sync, configure a recurring schedule in Connectors to keep chains up-to-date automatically.
                </div>
              </div>
            )}

            {/* SCIM 2.0 Protocol Info */}
            {igaSource && (
              <div style={{ padding: '10px 14px', background: '#eff6ff', borderRadius: 8, border: '1px solid #bfdbfe' }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#1e40af', marginBottom: 4 }}>
                  <Database size={10} style={{ display: 'inline', marginRight: 4, verticalAlign: -1 }} />
                  SCIM 2.0 Protocol Details
                </div>
                <div style={{ fontSize: 10, color: '#3b82f6', lineHeight: 1.7 }}>
                  <strong>User Schema:</strong> urn:ietf:params:scim:schemas:core:2.0:User<br />
                  <strong>Enterprise Ext:</strong> urn:ietf:params:scim:schemas:extension:enterprise:2.0:User (manager relationship)<br />
                  <strong>Supported Ops:</strong> GET, POST, PUT, PATCH (RFC 7644 §3.5.2), DELETE<br />
                  <strong>Filter Support:</strong> userName eq, externalId eq<br />
                  <strong>Manager Chain:</strong> Walks manager.value → auto-generates tiered escalation policies (4h → 8h → 24h SLA)
                </div>
              </div>
            )}
          </div>
        </Modal>
      )}

      {/* ========== MODALS ========== */}

      {/* User Add/Edit Modal */}
      <Modal isOpen={showUserModal} title={editingUser ? `Edit: ${editingUser.name || editingUser.email}` : 'Add New User'}
        onClose={() => setShowUserModal(false)}
        actions={<>
          <button onClick={() => setShowUserModal(false)} style={btnBase}>Cancel</button>
          <button onClick={handleSaveUser} style={btnPrimary}>{editingUser ? 'Save Changes' : 'Create User'}</button>
        </>}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={labelStyle}>First Name *</label>
              <input value={userForm.firstName} onChange={(e) => setUserForm({ ...userForm, firstName: e.target.value })}
                style={inputStyle} placeholder="Alice" />
            </div>
            <div>
              <label style={labelStyle}>Last Name</label>
              <input value={userForm.lastName} onChange={(e) => setUserForm({ ...userForm, lastName: e.target.value })}
                style={inputStyle} placeholder="Johnson" />
            </div>
          </div>
          <div>
            <label style={labelStyle}>Email *</label>
            <input type="email" value={userForm.email} disabled={!!editingUser}
              onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
              style={{ ...inputStyle, ...(editingUser ? { background: '#f4f4f5', color: '#a1a1aa' } : {}) }}
              placeholder="alice@acme.com" />
          </div>
          {!editingUser && (
            <>
              <div>
                <label style={labelStyle}>Password *</label>
                <input type="password" value={userForm.password}
                  onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                  style={inputStyle} placeholder="Min 8 chars, letter + number + special" autoComplete="new-password" />
                {userForm.password && (
                  <div style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <div style={{ display: 'flex', gap: 2, flex: 1 }}>
                      {[1,2,3,4].map((i) => (
                        <div key={i} style={{ flex: 1, height: 3, borderRadius: 2, background: getPasswordStrength(userForm.password) >= i ? PASSWORD_STRENGTH_COLORS[getPasswordStrength(userForm.password)] : '#e4e4e7' }} />
                      ))}
                    </div>
                    <span style={{ fontSize: 10, color: PASSWORD_STRENGTH_COLORS[getPasswordStrength(userForm.password)], fontWeight: 600 }}>
                      {PASSWORD_STRENGTH_LABELS[getPasswordStrength(userForm.password)]}
                    </span>
                  </div>
                )}
                <div style={{ fontSize: 10, color: '#a8a29e', marginTop: 3 }}>{getPasswordRequirements()}</div>
              </div>
              <div>
                <label style={labelStyle}>Confirm Password *</label>
                <input type="password" value={userForm.confirmPassword || ''}
                  onChange={(e) => setUserForm({ ...userForm, confirmPassword: e.target.value })}
                  style={{ ...inputStyle, borderColor: userForm.confirmPassword && userForm.confirmPassword !== userForm.password ? '#ef4444' : '#e4e4e7' }}
                  placeholder="Re-enter password" autoComplete="new-password" />
                {userForm.confirmPassword && userForm.confirmPassword !== userForm.password && (
                  <div style={{ fontSize: 10, color: '#ef4444', marginTop: 3 }}>Passwords do not match</div>
                )}
              </div>
            </>
          )}
          <div>
            <label style={labelStyle}>Role</label>
            <select value={userForm.role} onChange={(e) => setUserForm({ ...userForm, role: e.target.value })}
              style={inputStyle}>
              {ROLE_OPTIONS.filter((r) => canManageRole(r) || r === userForm.role).map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
            <div style={{ fontSize: 11, color: '#a1a1aa', marginTop: 4 }}>
              {currentRole === 'Super Admin'
                ? 'As Super Admin you can assign any role including Super Admin.'
                : `You can only assign roles below your own level (${currentRole}).`}
            </div>
            {editingUser && (
              <div style={{ fontSize: 10, color: '#f59e0b', marginTop: 2 }}>
                Note: This sets the user's base role. The membership role below takes precedence for org-level access.
              </div>
            )}
          </div>

          {/* Org/Tenant Memberships — only shown when editing */}
          {editingUser && (
            <div style={{ borderTop: '1px solid #e4e4e7', paddingTop: 16, marginTop: 8 }}>
              <label style={{ ...labelStyle, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
                <Building2 size={14} /> Org / Tenant Memberships
              </label>

              {loadingMemberships ? (
                <div style={{ fontSize: 12, color: '#a1a1aa', padding: 8 }}>Loading memberships…</div>
              ) : (
                <>
                  {/* Existing memberships */}
                  {userMemberships.length === 0 && (
                    <div style={{ fontSize: 12, color: '#a1a1aa', padding: '4px 0' }}>No memberships yet.</div>
                  )}
                  {userMemberships.map((m) => {
                    const orgName = orgMap[m.orgId]?.name || m.orgId?.slice(0, 8) || '—';
                    const tenName = tenantMap[m.tenantId]?.name || m.tenantId?.slice(0, 8) || '—';
                    return (
                      <div key={m.id} style={{
                        display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px',
                        background: m.isDefault ? '#f5f5f4' : '#fafafa', borderRadius: 6, marginBottom: 6,
                        border: m.isDefault ? '1px solid #a5d8d0' : '1px solid #e4e4e7',
                      }}>
                        <div style={{ flex: 1, fontSize: 12 }}>
                          <strong>{orgName}</strong> / {tenName}
                          <span style={{ marginLeft: 8, color: '#71717a' }}>({m.role})</span>
                          {m.isDefault && (
                            <span style={{ marginLeft: 6, background: '#dbeafe', color: '#1e40af', padding: '1px 6px', borderRadius: 4, fontSize: 10, fontWeight: 600 }}>
                              PRIMARY
                            </span>
                          )}
                        </div>
                        {!m.isDefault && canEditUsers && (
                          <button onClick={() => handleSetDefaultMembership(m.id)}
                            style={{ ...btnBase, padding: '2px 6px', fontSize: 10 }} title="Set as primary">
                            <Star size={10} /> Primary
                          </button>
                        )}
                        {canEditUsers && !m.isDefault && (
                          <button onClick={() => handleRemoveMembership(m.id)}
                            style={{ ...btnDanger, padding: '2px 6px', fontSize: 10 }} title="Remove">
                            <Trash2 size={10} />
                          </button>
                        )}
                        {canEditUsers && m.isDefault && (
                          <span style={{ fontSize: 9, color: '#a1a1aa', fontStyle: 'italic' }}>
                            Assign another primary first
                          </span>
                        )}
                      </div>
                    );
                  })}

                  {/* Add new membership form */}
                  {canEditUsers && (
                    <div style={{ marginTop: 8, padding: 10, background: '#f9fafb', borderRadius: 6, border: '1px solid #e4e4e7' }}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: '#52525b', marginBottom: 8 }}>Add Membership</div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                        <div>
                          <label style={{ fontSize: 10, color: '#71717a' }}>Organization</label>
                          <select value={newMembership.orgId}
                            onChange={(e) => setNewMembership({ ...newMembership, orgId: e.target.value, tenantId: '' })}
                            style={{ ...inputStyle, fontSize: 11, padding: '4px 6px' }}>
                            <option value="">Select org…</option>
                            {(orgs || []).map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                          </select>
                        </div>
                        <div>
                          <label style={{ fontSize: 10, color: '#71717a' }}>Tenant</label>
                          <select value={newMembership.tenantId}
                            onChange={(e) => setNewMembership({ ...newMembership, tenantId: e.target.value })}
                            style={{ ...inputStyle, fontSize: 11, padding: '4px 6px' }}>
                            <option value="">Select tenant…</option>
                            {filteredTenantsForOrg.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                          </select>
                        </div>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 8, marginTop: 8 }}>
                        <select value={newMembership.role}
                          onChange={(e) => setNewMembership({ ...newMembership, role: e.target.value })}
                          style={{ ...inputStyle, fontSize: 11, padding: '4px 6px' }}>
                          {ROLE_OPTIONS.filter((r) => canManageRole(r)).map((r) => (
                            <option key={r} value={r}>{r}</option>
                          ))}
                        </select>
                        <button onClick={handleAddMembership}
                          style={{ ...btnPrimary, fontSize: 11, padding: '4px 10px' }}>
                          <Plus size={12} /> Add
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </Modal>

      {/* Group Add/Edit Modal */}
      <Modal isOpen={showGroupModal} title={editingGroup ? `Edit: ${editingGroup.name}` : 'Create New Group'}
        onClose={() => setShowGroupModal(false)}
        actions={<>
          <button onClick={() => setShowGroupModal(false)} style={btnBase}>Cancel</button>
          <button onClick={handleSaveGroup} style={btnPrimary}>{editingGroup ? 'Save Changes' : 'Create Group'}</button>
        </>}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <label style={labelStyle}>Group Name *</label>
            <input value={groupForm.name} onChange={(e) => setGroupForm({ ...groupForm, name: e.target.value })}
              style={inputStyle} placeholder="e.g. SOC Analysts, Security Engineering" />
          </div>
          <div>
            <label style={labelStyle}>Description</label>
            <textarea value={groupForm.description}
              onChange={(e) => setGroupForm({ ...groupForm, description: e.target.value })}
              style={{ ...inputStyle, fontFamily: 'inherit', minHeight: 80 }}
              placeholder="What is this group responsible for?" />
          </div>
          <div style={{ fontSize: 11, color: '#a1a1aa', lineHeight: 1.5 }}>
            After creating the group, expand it to add members using the search-based lookup.
          </div>
        </div>
      </Modal>

      {/* Assignment Modal */}
      <Modal isOpen={!!showAssignModal} title="Create Assignment"
        onClose={() => setShowAssignModal(null)}
        actions={<>
          <button onClick={() => setShowAssignModal(null)} style={btnBase}>Cancel</button>
          <button onClick={handleCreateAssignment} style={btnPrimary}>Create Assignment</button>
        </>}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <label style={labelStyle}>Assign a User</label>
            <select value={assignForm.userId}
              onChange={(e) => setAssignForm({ ...assignForm, userId: e.target.value, groupId: '' })}
              style={inputStyle}>
              <option value="">— Select user —</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>{u.name || u.email} ({u.role})</option>
              ))}
            </select>
          </div>
          <div style={{ textAlign: 'center', fontSize: 12, color: '#a1a1aa' }}>— OR —</div>
          <div>
            <label style={labelStyle}>Assign a Group</label>
            <select value={assignForm.groupId}
              onChange={(e) => setAssignForm({ ...assignForm, groupId: e.target.value, userId: '' })}
              style={inputStyle}>
              <option value="">— Select group —</option>
              {groups.map((g) => (
                <option key={g.id} value={g.id}>{g.name} ({g.members?.length || 0} members)</option>
              ))}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Access Level</label>
            <select value={assignForm.accessLevel}
              onChange={(e) => setAssignForm({ ...assignForm, accessLevel: e.target.value })}
              style={inputStyle}>
              {ACCESS_LEVELS.map((l) => (
                <option key={l} value={l}>{l.charAt(0).toUpperCase() + l.slice(1)}</option>
              ))}
            </select>
            <div style={{ fontSize: 11, color: '#a1a1aa', marginTop: 4 }}>
              <strong>Execute:</strong> Can invoke agent skills &bull;
              <strong> Manage:</strong> Can configure settings &bull;
              <strong> Admin:</strong> Full control
            </div>
          </div>
        </div>
      </Modal>

      {/* Connector Group Add/Edit Modal */}
      <Modal isOpen={showConnGroupModal}
        title={editingConnGroup ? `Edit: ${editingConnGroup.name}` : 'Create Connector Group'}
        onClose={() => setShowConnGroupModal(false)}
        actions={<>
          <button onClick={() => setShowConnGroupModal(false)} style={btnBase}>Cancel</button>
          <button onClick={handleSaveConnGroup} style={btnPrimary}>
            {editingConnGroup ? 'Save Changes' : 'Create Group'}
          </button>
        </>}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <label style={labelStyle}>Group Name *</label>
            <input value={connGroupForm.name}
              onChange={(e) => setConnGroupForm({ ...connGroupForm, name: e.target.value })}
              style={inputStyle} placeholder="e.g. Production APIs, Security Connectors" />
          </div>
          <div>
            <label style={labelStyle}>Description</label>
            <textarea value={connGroupForm.description}
              onChange={(e) => setConnGroupForm({ ...connGroupForm, description: e.target.value })}
              style={{ ...inputStyle, fontFamily: 'inherit', minHeight: 80 }}
              placeholder="What connectors will this group contain?" />
          </div>
          <div style={{ fontSize: 11, color: '#a1a1aa', lineHeight: 1.5 }}>
            After creating the group, expand it to add connectors from your installed connector list.
          </div>
        </div>
      </Modal>

      {/* Connector Assignment Modal */}
      <Modal isOpen={!!showConnAssignModal} title="Assign Connector to Agent"
        onClose={() => setShowConnAssignModal(null)}
        actions={<>
          <button onClick={() => setShowConnAssignModal(null)} style={btnBase}>Cancel</button>
          <button onClick={handleCreateConnectorAssignment} style={btnPrimary}>Assign</button>
        </>}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <label style={labelStyle}>Assign a Connector</label>
            <select value={connAssignForm.connectorId}
              onChange={(e) => setConnAssignForm({ ...connAssignForm, connectorId: e.target.value, connectorGroupId: '' })}
              style={inputStyle}>
              <option value="">— Select connector —</option>
              {connectors.map((c) => (
                <option key={c.id} value={c.id}>{c.name} ({c.type} · {c.authMethod || 'N/A'})</option>
              ))}
            </select>
          </div>
          <div style={{ textAlign: 'center', fontSize: 12, color: '#a1a1aa' }}>— OR —</div>
          <div>
            <label style={labelStyle}>Assign a Connector Group</label>
            <select value={connAssignForm.connectorGroupId}
              onChange={(e) => setConnAssignForm({ ...connAssignForm, connectorGroupId: e.target.value, connectorId: '' })}
              style={inputStyle}>
              <option value="">— Select connector group —</option>
              {connectorGroups.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name} ({(g.connectors || []).length} connector{(g.connectors || []).length !== 1 ? 's' : ''})
                </option>
              ))}
            </select>
          </div>
          <div style={{ fontSize: 11, color: '#a1a1aa', lineHeight: 1.5, padding: '8px 12px', background: '#f9fafb', borderRadius: 6 }}>
            Once connectors are assigned, only those connectors (and connectors in assigned groups)
            will be available during this agent's lifecycle phases.
            If no connectors are assigned, all connectors remain available.
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation */}
      <Modal isOpen={!!showDeleteConfirm}
        title={`Delete ${showDeleteConfirm?.type === 'user' ? 'User' : 'Group'}`}
        onClose={() => setShowDeleteConfirm(null)}
        actions={<>
          <button onClick={() => setShowDeleteConfirm(null)} style={btnBase}>Cancel</button>
          <button onClick={showDeleteConfirm?.type === 'user' ? handleDeleteUser : handleDeleteGroup}
            style={btnDanger}>Delete</button>
        </>}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          <AlertTriangle size={20} style={{ color: '#d97706', flexShrink: 0, marginTop: 2 }} />
          <p style={{ fontSize: 14, color: '#52525b', margin: 0 }}>
            Are you sure you want to delete <strong>"{showDeleteConfirm?.item?.name || showDeleteConfirm?.item?.email}"</strong>?
            This action cannot be undone.
          </p>
        </div>
      </Modal>

      {/* ========== Invite User Modal ========== */}
      <Modal isOpen={showInviteModal} title="Invite User to Platform"
        onClose={() => { setShowInviteModal(false); setInviteResult(null); }}
        actions={!inviteResult ? <>
          <button onClick={() => setShowInviteModal(false)} style={btnBase}>Cancel</button>
          <button onClick={handleSendInvite} disabled={inviteLoading}
            style={{ ...btnPrimary, background: inviteLoading ? '#d6d3d1' : '#0d9488', cursor: inviteLoading ? 'wait' : 'pointer', opacity: inviteLoading ? 0.7 : 1 }}>
            {inviteLoading ? <><Activity size={14} className="spin" /> Validating...</> : <><Send size={14} /> Send Invite</>}
          </button>
        </> : <>
          <button onClick={() => { setShowInviteModal(false); setInviteResult(null); }} style={btnPrimary}>Done</button>
        </>}>

        {!inviteResult ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ padding: '10px 14px', background: '#e3f4f1', border: '1px solid #a5d8d0', borderRadius: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                <Mail size={14} color="#085041" />
                <span style={{ fontSize: 12, fontWeight: 600, color: '#085041' }}>Secure Registration Invitation</span>
              </div>
              <div style={{ fontSize: 11, color: '#085041' }}>
                The user will receive a one-time registration link valid for 24 hours. They will set their own password upon registration.
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label style={labelStyle}>First Name *</label>
                <input value={inviteForm.firstName} onChange={(e) => setInviteForm({ ...inviteForm, firstName: e.target.value })}
                  style={inputStyle} placeholder="Alice" />
              </div>
              <div>
                <label style={labelStyle}>Last Name</label>
                <input value={inviteForm.lastName} onChange={(e) => setInviteForm({ ...inviteForm, lastName: e.target.value })}
                  style={inputStyle} placeholder="Johnson" />
              </div>
            </div>

            <div>
              <label style={labelStyle}>Email *</label>
              <input type="email" value={inviteForm.email}
                onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                style={inputStyle} placeholder="alice@acme.com" />
            </div>

            <div>
              <label style={labelStyle}>Organization *</label>
              <select value={inviteForm.orgId} onChange={(e) => {
                const newOrgId = e.target.value;
                const firstTenant = tenants.filter((t) => t.orgId === newOrgId)[0]?.id || '';
                setInviteForm({ ...inviteForm, orgId: newOrgId, tenantId: firstTenant });
              }}
                style={inputStyle}>
                <option value="">Select organization...</option>
                {orgs.map((o) => (
                  <option key={o.id} value={o.id}>{o.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label style={labelStyle}>Tenant *</label>
              <select value={inviteForm.tenantId} onChange={(e) => setInviteForm({ ...inviteForm, tenantId: e.target.value })}
                style={inputStyle}>
                <option value="">Select tenant...</option>
                {tenants.filter((t) => t.orgId === inviteForm.orgId).map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label style={labelStyle}>Role</label>
              <select value={inviteForm.role} onChange={(e) => setInviteForm({ ...inviteForm, role: e.target.value })}
                style={inputStyle}>
                {ROLE_OPTIONS.filter((r) => canManageRole(r)).map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
              <div style={{ fontSize: 11, color: '#a1a1aa', marginTop: 4 }}>
                You can only assign roles below your own level ({currentRole}).
              </div>
            </div>

            {/* Inline error display */}
            {inviteError && (
              <div style={{
                padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca',
                borderRadius: 8, display: 'flex', alignItems: 'flex-start', gap: 8,
              }}>
                <AlertTriangle size={16} color="#dc2626" style={{ flexShrink: 0, marginTop: 1 }} />
                <div style={{ fontSize: 12, color: '#991b1b', lineHeight: 1.5 }}>{inviteError}</div>
              </div>
            )}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ textAlign: 'center', padding: '16px 0' }}>
              <div style={{ width: 48, height: 48, borderRadius: '50%', background: '#e3f4f1', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12 }}>
                <Check size={24} color="#0d9488" />
              </div>
              <h3 style={{ fontSize: 16, fontWeight: 700, color: '#18181b', margin: '0 0 4px 0' }}>Invitation Sent</h3>
              <p style={{ fontSize: 13, color: '#71717a', margin: 0 }}>
                An invitation email workflow has been triggered for <strong>{inviteResult.email}</strong>
              </p>
            </div>

            <div style={{ background: '#f5f5f4', borderRadius: 8, padding: 16, fontSize: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ color: '#71717a' }}>Name</span>
                <span style={{ color: '#18181b', fontWeight: 600 }}>{inviteResult.firstName} {inviteResult.lastName}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ color: '#71717a' }}>Role</span>
                <span style={{ color: '#18181b', fontWeight: 600 }}>{inviteResult.role}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ color: '#71717a' }}>Organization</span>
                <span style={{ color: '#18181b', fontWeight: 600 }}>{orgs.find((o) => o.id === inviteResult.orgId)?.name || '—'}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ color: '#71717a' }}>Tenant</span>
                <span style={{ color: '#18181b', fontWeight: 600 }}>{tenants.find((t) => t.id === inviteResult.tenantId)?.name || '—'}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ color: '#71717a' }}>Expires</span>
                <span style={{ color: '#085041', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
                  <Clock size={12} /> 24 hours
                </span>
              </div>
            </div>

            {inviteResult.registrationUrl && (
              <div>
                <label style={{ ...labelStyle, marginBottom: 6 }}>Registration Link (one-time use)</label>
                <div style={{ display: 'flex', gap: 4 }}>
                  <input readOnly value={inviteResult.registrationUrl}
                    style={{ ...inputStyle, fontSize: 11, fontFamily: "'IBM Plex Mono', monospace", background: '#fafaf9', flex: 1 }} />
                  <button onClick={() => {
                    navigator.clipboard.writeText(inviteResult.registrationUrl);
                    addToast('Link copied to clipboard', 'success');
                  }} style={{ ...btnBase, padding: '6px 10px' }}>
                    <Copy size={14} />
                  </button>
                </div>
                <div style={{ fontSize: 10, color: '#a8a29e', marginTop: 4 }}>
                  This link can only be used once and expires in 24 hours.
                </div>
                <div style={{ marginTop: 8, padding: '8px 12px', background: '#e3f4f1', border: '1px solid #a5d8d0', borderRadius: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Activity size={12} color="#0d9488" />
                  <span style={{ fontSize: 11, color: '#085041' }}>
                    An invitation email workflow has been triggered via the <strong>Email SMTP</strong> connector. Check <strong>Workflow Canvas</strong> for execution status.
                  </span>
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>

    </V2PageShell>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------
function StatCard({ icon, label, value, sub, color }) {
  return (
    <div style={{
      background: '#fff', border: '1px solid #e4e4e7', borderRadius: 8, padding: '14px 18px',
      display: 'flex', alignItems: 'center', gap: 14,
    }}>
      <div style={{
        width: 38, height: 38, borderRadius: 8,
        background: `${color}12`, color,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>{icon}</div>
      <div>
        <div style={{ fontSize: 20, fontWeight: 700, color: '#18181b' }}>{value}</div>
        <div style={{ fontSize: 11, color: '#71717a' }}>{label}{sub ? ` • ${sub}` : ''}</div>
      </div>
    </div>
  );
}

function Avatar({ name, size = 32, fontSize = 12 }) {
  const letter = (name || '?')[0]?.toUpperCase();
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', background: '#e3f4f1',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize, fontWeight: 700, color: '#0d9488', flexShrink: 0,
    }}>{letter}</div>
  );
}

function RoleBadge({ role, small }) {
  const c = ROLE_COLORS[role] || ROLE_COLORS['Viewer'];
  return (
    <span style={{
      display: 'inline-block', padding: small ? '1px 6px' : '2px 8px', borderRadius: 4,
      fontSize: small ? 10 : 11, fontWeight: 600,
      background: c.bg, color: c.text, border: `1px solid ${c.border}`,
    }}>{role || 'Viewer'}</span>
  );
}

function EmptyState({ msg }) {
  return (
    <div style={{
      textAlign: 'center', padding: 48, color: '#a1a1aa', fontSize: 13,
      background: '#fff', border: '1px solid #e4e4e7', borderRadius: 8,
    }}>{msg}</div>
  );
}
